'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type LangCode = 'en' | 'ar' | 'zh' | 'ms' | 'th' | 'vi';

const translations: Record<LangCode, Record<string, string>> = {
  en: {
    // Hero
    hero_badge: '#1 Crypto Trading Platform',
    hero_heading_1: 'Trade Crypto',
    hero_heading_2: 'like a pro',
    hero_subtitle: 'Advanced trading tools, lightning-fast execution, and unmatched security. Join millions of traders worldwide.',
    hero_bonus_title: '100% Deposit Bonus',
    hero_bonus_desc: 'Deposit $1,000 and trade with $2,000 instantly',
    hero_cta_start: 'Start Trading',
    hero_cta_explore: 'Explore Markets',
    hero_feature_secure: 'Secure Trading',
    hero_feature_realtime: 'Real-time Data',
    hero_feature_support: '24/7 Support',
    hero_feature_deposit: 'Instant Deposit',
    hero_feature_withdraw: 'Instant Withdrawal (Min $10)',
    // Footer
    footer_desc: 'Tradiglo provides a fundamental analysis of the crypto market. In addition to tracking price, volume and market capitalisation, Tradiglo tracks community growth, open-source code development, major events and on-chain metrics.',
    footer_newsletter_title: 'Interested to stay up-to-date with cryptocurrencies?',
    footer_newsletter_desc: 'Get the latest crypto news, updates, and reports by subscribing to our free newsletter.',
    footer_newsletter_placeholder: 'Enter your email address',
    footer_newsletter_btn: 'Subscribe',
    footer_copyright: '© 2026 Tradiglo. All Rights Reserved.',
    footer_disclaimer_label: 'IMPORTANT DISCLAIMER:',
    footer_disclaimer_text: 'All content provided herein our website, hyperlinked sites, associated applications, forums, blogs, social media accounts and other platforms ("Site") is for your general information only, procured from third party sources. We make no warranties of any kind in relation to our content, including but not limited to accuracy and updatedness. No part of the content that we provide constitutes financial advice, legal advice or any other form of advice meant for your specific reliance for any purpose. Any use or reliance on our content is solely at your own risk and discretion. You should conduct your own research, review, analyse and verify our content before relying on them. Trading is a highly risky activity that can lead to major losses, please therefore consult your financial advisor before making any decision. No content on our Site is meant to be a solicitation or offer.',
    // Nav
    nav_markets: 'Markets',
    nav_copy_trading: 'Copy Trading',
    nav_screener: 'Screener',
    nav_news: 'News',
    nav_affiliate: 'Affiliate',
    nav_community: 'Community',
    nav_dashboard: 'Dashboard',
    nav_sign_in: 'Sign In',
    nav_sign_out: 'Sign Out',
    nav_get_started: 'Get Started',
    // Footer categories
    footer_cat_resources: 'Resources',
    footer_cat_support: 'Support',
    footer_cat_about: 'About Tradiglo',
    footer_cat_community: 'Community',
    // MarketsTable
    markets_tab_all: 'All',
    markets_tab_highlights: 'Highlights',
    markets_tab_categories: 'Categories',
    markets_tab_mobile_mining: 'Mobile Mining',
    markets_tab_quantum: 'Quantum-Resistant',
    markets_tab_sports: 'Sports',
    markets_customize: 'Customize',
    markets_label_all: 'All Markets',
    markets_label_highlights: 'Highlights',
    markets_label_categories: 'Categories',
    markets_label_mobile_mining: 'Mobile Mining',
    markets_label_quantum: 'Quantum-Resistant',
    markets_label_sports: 'Sports',
    markets_coins_suffix: 'Coins',
    markets_refresh: 'Refresh',
    markets_fetching: 'Fetching live market data...',
    markets_try_again: 'Try Again',
    markets_no_coins: 'No coins found in this category from current page data.',
    markets_no_coins_hint: 'Try refreshing or browsing a different page.',
    markets_col_coin: 'Coin',
    markets_col_price: 'Price',
    markets_col_1h: '1h',
    markets_col_24h: '24h',
    markets_col_7d: '7d',
    markets_col_volume: '24h Volume',
    markets_col_marketcap: 'Market Cap',
    markets_col_chart: 'Last 7 Days',
    markets_buy: 'Buy',
    markets_showing: 'Showing',
    markets_to: 'to',
    markets_of: 'of',
    markets_results: 'results',
    markets_rows: 'Rows',
    // TopMovers
    top_movers_title: 'Top Movers',
    top_movers_live: 'Live',
    // CryptoCards
    crypto_label: 'CRYPTO',
    // TickerTape — no translatable static labels (all dynamic data)
    // Dashboard
    dash_win_rate: 'Win Rate',
    dash_balance: 'Balance',
    dash_profit: 'Net P&L',
    dash_open: 'Open',
    dash_open_trades: 'Open Trades',
    dash_close_all: 'Close All',
    dash_open_count: 'open',
    dash_no_open_trades: 'No open trades',
    dash_open_trades: 'Open Trades',
    dash_close_all_confirm_title: 'Close all open trades?',
    dash_close_all_confirm_desc: 'All {count} open trade{plural} will be closed at the current chart price.',
    dash_cancel: 'Cancel',
    dash_copy_following_badge: 'Following',
    dash_copy_win_rate: 'Win Rate',
    dash_copy_active_followers: 'Active Followers',
    dash_copy_min_balance: 'Min Balance',
    dash_copy_history_title: 'Copy Trade History',
    dash_copy_no_history: 'No copy trade history yet',
    dash_copy_guarantee: '100% Balance Guarantee on Loss',
    dash_copy_copying_msg: 'You are currently copying Tradiglo trades',
    dash_copy_ready_msg: 'Ready to copy Tradiglo trades?',
    dash_copy_stop_follow: 'Stop Follow',
    dash_copy_follow_btn: 'Follow Tradiglo',
    dash_copy_not_available: 'Copy Trade not available',
    dash_copy_invested: 'invested',
    dash_copy_score_activity: 'Activity',
    dash_copy_score_probability: 'Probability',
    dash_copy_score_reliability: 'Reliability',
    dash_copy_score_popularity: 'Popularity',
    dash_copy_score_experience: 'Experience',
    // Copy Trading Page
    ct_live_badge: 'Copy Trading — Powered by Tradiglo',
    ct_hero_title_1: 'Copy Our',
    ct_hero_title_2: 'Strategy',
    ct_hero_subtitle: "Follow Tradiglo's proprietary trading strategy — the highest verified win rate in the industry. Available exclusively for members with a minimum balance of $",
    ct_checking_balance: 'Checking balance...',
    ct_activate_btn: 'Activate Copy Trading',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'Get Started Free',
    ct_sign_in_btn: 'Sign In',
    ct_balance_qualifies: 'Your balance $',
    ct_balance_qualifies_suffix: 'qualifies ✓',
    ct_need_more: 'Need $',
    ct_need_more_suffix: 'more to qualify (min $',
    ct_stat_win_rate: 'Overall Win Rate',
    ct_stat_win_rate_sub: '+5 years verified',
    ct_stat_total_trades: 'Total Trades',
    ct_stat_total_trades_sub: 'January 2020 – Present',
    ct_stat_total_profit: 'Total Profit Generated',
    ct_stat_total_profit_sub: 'Cumulative',
    ct_stat_industry: 'Industry Average',
    ct_stat_industry_sub: 'Competitors',
    ct_guarantee_title: '90% Win Rate Guarantee',
    ct_guarantee_sub: 'Backed by +5 years of verified performance data',
    ct_guarantee_desc: 'Tradiglo guarantees a minimum <strong>90% win rate</strong> on all copy trading signals — the highest in the industry. Our proprietary algorithm has consistently outperformed the industry average of 60–70% for over 5 consecutive years.',
    ct_compare_title: 'How We Compare',
    ct_compare_sub: 'Win rate comparison against industry benchmarks',
    ct_history_title: '+5-Year Trade History',
    ct_history_sub: 'Verified monthly performance data — January 2020 to Present',
    ct_col_month: 'Month',
    ct_col_trades: 'Trades',
    ct_col_wins: 'Wins',
    ct_col_win_rate: 'Win Rate',
    ct_col_profit: 'Profit',
    ct_how_title: 'How It Works',
    ct_how_sub: 'Three simple steps to start copying our strategy',
    ct_step1_title: 'Create Account',
    ct_step1_desc: 'Sign up for free and complete your profile verification.',
    ct_step2_title: 'Deposit $1,499+',
    ct_step2_desc: 'Fund your real account with a minimum of $1,499 USD to unlock copy trading.',
    ct_step3_title: 'Activate & Earn',
    ct_step3_desc: 'Enable copy trading and our algorithm automatically mirrors every trade for you.',
    ct_cta_title_1: 'Ready to Copy the',
    ct_cta_title_2: 'Best Strategy?',
    ct_cta_sub: 'Join thousands of members already benefiting from Tradiglo\'s',
    ct_cta_sub2: 'win rate copy trading system.',
    ct_cta_start_btn: 'Start Copy Trading',
    ct_cta_go_btn: 'Go to Copy Trading Dashboard',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: 'Crypto Screener',
    sc_subtitle_prefix: 'Filter',
    sc_subtitle_suffix: 'cryptocurrencies by price, volume, and movement',
    sc_results: 'results',
    sc_filters_btn: 'Filters',
    sc_reset_btn: 'Reset',
    sc_refresh_btn: '↻ Refresh',
    sc_filter_movement: 'Movement',
    sc_filter_price: 'Price Range (USD)',
    sc_filter_volume: 'Volume 24h (in Millions)',
    sc_filter_change: '24h Change (%)',
    sc_filter_marketcap: 'Min Market Cap (in Millions)',
    sc_movement_all: 'all',
    sc_movement_gainers: 'gainers',
    sc_movement_losers: 'losers',
    sc_error_suffix: '— CoinGecko free API may have rate limits. Please try again in a moment.',
    sc_col_name: 'Name',
    sc_col_price: 'Price',
    sc_col_24h: '24h %',
    sc_col_high: '24h High',
    sc_col_low: '24h Low',
    sc_col_volume: 'Volume',
    sc_col_marketcap: 'Market Cap',
    sc_no_match: 'No coins match your filters. Try adjusting the criteria.',
    // News Page
    news_title: 'Crypto News',
    news_subtitle: 'Latest cryptocurrency and blockchain news, updated automatically',
    news_cat_all: 'All Crypto',
    news_cat_bitcoin: 'Bitcoin',
    news_cat_ethereum: 'Ethereum',
    news_cat_defi: 'DeFi',
    news_showing: 'Showing page',
    news_of: 'of',
    news_articles: 'unique articles',
    news_read_more: 'Read more',
    news_no_articles: 'No articles found for this category.',
    news_prev: '‹ Prev',
    news_next: 'Next ›',
    // Affiliate/Referral Page
    aff_badge: 'Affiliate Program',
    aff_hero_title_1: 'Earn',
    aff_hero_title_2: 'for Every Friend You Invite',
    aff_hero_subtitle: 'Invite friends to join Tradiglo and earn 25% of their total initial deposit — forever, with no expiry, as long as you both remain members.',
    aff_join_btn: 'Join & Start Referring',
    aff_signin_btn: 'Sign In to Dashboard',
    aff_stat1_label: 'Commission on Initial Deposit',
    aff_stat2_label: 'Minimum Payout Threshold',
    aff_stat3_label: 'Lifetime Validity',
    aff_how_title: 'How It Works',
    aff_how_sub: 'Four simple steps to start earning with the Tradiglo Affiliate Program.',
    aff_step1_title: 'Sign Up & Get Your Link',
    aff_step1_desc: 'Create your Tradiglo account and receive a unique affiliate link from your dashboard.',
    aff_step2_title: 'Invite Your Network',
    aff_step2_desc: 'Share your affiliate link with friends, family, or your community through any channel.',
    aff_step3_title: 'They Join & Deposit',
    aff_step3_desc: 'When someone registers via your link and makes their first deposit, the affiliate is recorded.',
    aff_step4_title: 'Earn 25% Reward',
    aff_step4_desc: 'You earn 25% of the total initial deposit made by each member you referred.',
    aff_details_title: 'Program Details',
    aff_details_sub: 'Everything you need to know about the Tradiglo Affiliate Program.',
    aff_detail1_title: 'Commission Rate',
    aff_detail1_value: '25% of Initial Deposit',
    aff_detail1_desc: 'You receive 25% of the total first deposit made by every member you refer.',
    aff_detail2_title: 'Payout Threshold',
    aff_detail2_value: '$500 Minimum',
    aff_detail2_desc: 'Rewards are released once your total accumulated affiliate earnings reach $500.',
    aff_detail3_title: 'Validity',
    aff_detail3_value: 'Lifetime — No Expiry',
    aff_detail3_desc: 'Your affiliate relationship is permanent for as long as both you and the referred member stay active.',
    aff_detail4_title: 'Affiliate Limit',
    aff_detail4_value: 'Unlimited Affiliates',
    aff_detail4_desc: 'There is no cap on the number of people you can refer. The more you invite, the more you earn.',
    aff_faq_title: 'Frequently Asked Questions',
    aff_faq_sub: 'Got questions? We have answers.',
    aff_faq1_q: 'How much do I earn per affiliate?',
    aff_faq1_a: 'You earn 25% of the total initial deposit made by each new member you successfully refer to Tradiglo.',
    aff_faq2_q: 'When is the reward paid out?',
    aff_faq2_a: 'Rewards are paid out once your accumulated affiliate earnings reach $500. This threshold ensures efficient processing.',
    aff_faq3_q: 'How long does the affiliate program last?',
    aff_faq3_a: 'Your affiliate rewards are valid for as long as both you and the referred member remain active Tradiglo members — there is no expiry.',
    aff_faq4_q: 'Is there a limit to how many people I can refer?',
    aff_faq4_a: 'No limit. You can refer as many people as you like and earn 25% from each of their initial deposits.',
    aff_faq5_q: 'How do I track my affiliates?',
    aff_faq5_a: 'Once you are a member, your dashboard will display your affiliate link, the number of referred members, and your accumulated reward balance.',
    aff_faq6_q: 'Does the referred member get any benefit?',
    aff_faq6_a: 'Yes — referred members may receive a welcome bonus upon joining. Check the latest promotions on your dashboard for details.',
    aff_cta_title: 'Ready to Start Earning?',
    aff_cta_sub: 'Join Tradiglo today, get your unique affiliate link, and start earning 25% from every member you bring in — for life.',
    aff_cta_btn: 'Create Your Account',
    // Markets Page
    mkt_title: 'Markets',
    mkt_subtitle: 'Practice trading with virtual funds',
    mkt_demo_trades_left: 'demo trade',
    mkt_demo_trades_left_plural: 'demo trades',
    mkt_demo_trades_suffix: 'left',
    mkt_demo_balance: 'Demo Account Balance',
    mkt_stat_total_trades: 'Total Trades',
    mkt_stat_win_rate: 'Win Rate',
    mkt_stat_total_profit: 'Total Profit',
    mkt_stat_open_positions: 'Open Positions',
    mkt_trading_symbol: 'Trading Symbol',
    mkt_live_market: 'Live Market',
    mkt_potential_profit: 'Potential Profit',
    mkt_payout: 'Payout',
    mkt_open_positions_tab: 'Open Positions',
    mkt_history_tab: 'History',
    mkt_no_open_positions: 'No open positions. Place a trade to get started.',
    mkt_no_history: 'No trade history yet.',
    mkt_popular_assets: 'Popular Assets',
    mkt_popular_assets_sub: 'Track and trade the most popular stocks, cryptocurrencies, forex pairs, and commodities',
    mkt_entry: 'Entry:',
    mkt_invested: 'invested',
    // Dashboard missing keys
    dash_col_asset: 'Asset',
    dash_col_type: 'Type',
    dash_col_amount: 'Amount',
    dash_col_entry: 'Entry',
    dash_col_duration: 'Duration',
    dash_col_remaining: 'Remaining',
    dash_trade_history: 'Trade History',
    dash_trades_count: ' trade',
    dash_col_dir: 'Direction',
    dash_col_exit: 'Exit',
    dash_col_pl: 'P&L',
    dash_col_result: 'Result',
    dash_col_date: 'Date',
    dash_no_history: 'No trade history yet',
    dash_load_more: 'Load More',
    dash_loading: 'Loading...',
    dash_dur: 'Duration',
    dash_amt: 'Amount',
    dash_copying_trades: 'You are currently copying Tradiglo trades',
    dash_deposit: 'Deposit',
    dash_sell: 'Sell',
    dash_buy: 'Buy',
    dash_select_asset_hint: 'Select an asset to view chart',
    dash_select_asset_btn: 'Select Asset',
    dash_payout: 'payout',
    dash_nav_trade: 'Trade',
    dash_nav_history: 'History',
    dash_nav_copytrade: 'Copy Trade',
    dash_nav_affiliate: 'Affiliate',
    dash_nav_account: 'Account',
    dash_account_title: 'Account',
    dash_demo_balance: 'Demo Balance',
    dash_real_balance: 'Real Balance',
  },
  ar: {
    hero_badge: 'منصة تداول العملات المشفرة #1',
    hero_heading_1: 'تداول العملات المشفرة',
    hero_heading_2: 'كالمحترفين',
    hero_subtitle: 'أدوات تداول متقدمة، تنفيذ فائق السرعة، وأمان لا مثيل له. انضم إلى ملايين المتداولين حول العالم.',
    hero_bonus_title: 'مكافأة إيداع 100%',
    hero_bonus_desc: 'أودع 1,000 دولار وتداول بـ 2,000 دولار فوراً',
    hero_cta_start: 'ابدأ التداول',
    hero_cta_explore: 'استكشف الأسواق',
    hero_feature_secure: 'تداول آمن',
    hero_feature_realtime: 'بيانات فورية',
    hero_feature_support: 'دعم 24/7',
    hero_feature_deposit: 'إيداع فوري',
    hero_feature_withdraw: 'سحب فوري (الحد الأدنى 10$)',
    footer_desc: 'تقدم Tradiglo تحليلاً أساسياً لسوق العملات المشفرة. بالإضافة إلى تتبع السعر والحجم والقيمة السوقية، تتتبع Tradiglo نمو المجتمع وتطوير الكود مفتوح المصدر والأحداث الرئيسية والمقاييس على السلسلة.',
    footer_newsletter_title: 'هل تريد البقاء على اطلاع بآخر أخبار العملات المشفرة؟',
    footer_newsletter_desc: 'احصل على أحدث أخبار العملات المشفرة والتحديثات والتقارير بالاشتراك في نشرتنا الإخبارية المجانية.',
    footer_newsletter_placeholder: 'أدخل عنوان بريدك الإلكتروني',
    footer_newsletter_btn: 'اشترك',
    footer_copyright: '© 2026 Tradiglo. جميع الحقوق محفوظة.',
    footer_disclaimer_label: 'إخلاء مسؤولية مهم:',
    footer_disclaimer_text: 'جميع المحتويات المقدمة في موقعنا الإلكتروني والمواقع المرتبطة والتطبيقات المرتبطة والمنتديات والمدونات وحسابات وسائل التواصل الاجتماعي والمنصات الأخرى ("الموقع") هي لأغراض معلوماتية عامة فقط، مستمدة من مصادر طرف ثالث. لا نقدم أي ضمانات من أي نوع فيما يتعلق بمحتوانا. لا يُعدّ أي جزء من المحتوى الذي نقدمه نصيحة مالية أو قانونية. التداول نشاط عالي المخاطر قد يؤدي إلى خسائر كبيرة، لذا يُرجى استشارة مستشارك المالي قبل اتخاذ أي قرار.',
    nav_markets: 'الأسواق',
    nav_copy_trading: 'نسخ التداول',
    nav_screener: 'الفلتر',
    nav_news: 'الأخبار',
    nav_affiliate: 'الإحالة',
    nav_community: 'المجتمع',
    nav_dashboard: 'لوحة التحكم',
    nav_sign_in: 'تسجيل الدخول',
    nav_sign_out: 'تسجيل الخروج',
    nav_get_started: 'ابدأ الآن',
    footer_cat_resources: 'الموارد',
    footer_cat_support: 'الدعم',
    footer_cat_about: 'عن Tradiglo',
    footer_cat_community: 'المجتمع',
    markets_tab_all: 'الكل',
    markets_tab_highlights: 'المميزة',
    markets_tab_categories: 'الفئات',
    markets_tab_mobile_mining: 'التعدين المحمول',
    markets_tab_quantum: 'مقاوم للكم',
    markets_tab_sports: 'الرياضة',
    markets_customize: 'تخصيص',
    markets_label_all: 'جميع الأسواق',
    markets_label_highlights: 'المميزة',
    markets_label_categories: 'الفئات',
    markets_label_mobile_mining: 'التعدين المحمول',
    markets_label_quantum: 'مقاوم للكم',
    markets_label_sports: 'الرياضة',
    markets_coins_suffix: 'عملة',
    markets_refresh: 'تحديث',
    markets_fetching: 'جلب بيانات السوق المباشرة...',
    markets_try_again: '_TRY Again',
    markets_no_coins: 'لا توجد عملات في هذه الفئة من بيانات الصفحة الحالية.',
    markets_no_coins_hint: 'جرب التحديث أو تصفح صفحة مختلفة.',
    markets_col_coin: 'العملة',
    markets_col_price: 'السعر',
    markets_col_1h: '1س',
    markets_col_24h: '24س',
    markets_col_7d: '7أ',
    markets_col_volume: 'حجم 24س',
    markets_col_marketcap: 'القيمة السوقية',
    markets_col_chart: 'آخر 7 أيام',
    markets_buy: 'شراء',
    markets_showing: 'عرض',
    markets_to: 'إلى',
    markets_of: 'من',
    markets_results: 'نتيجة',
    markets_rows: 'صفوف',
    top_movers_title: 'أكثر تحركاً',
    top_movers_live: 'مباشر',
    crypto_label: 'كريبتو',
    // Dashboard
    dash_win_rate: 'معدل الفوز',
    dash_balance: 'الرصيد',
    dash_profit: 'صافي الربح',
    dash_open: 'مفتوح',
    dash_open_trades: 'الصفقات المفتوحة',
    dash_close_all: 'إغلاق الكل',
    dash_open_count: 'مفتوح',
    dash_no_open_trades: 'No open trades',
    dash_col_asset: 'الأصل',
    dash_col_type: 'النوع',
    dash_col_amount: 'المبلغ',
    dash_col_entry: 'الدخول',
    dash_col_duration: 'المدة',
    dash_col_remaining: 'المتبقي',
    dash_trade_history: 'سجل الصفقات',
    dash_trades_count: ' صفقة',
    dash_col_dir: 'الاتجاه',
    dash_col_exit: 'الخروج',
    dash_col_pl: 'الربح/الخسارة',
    dash_col_result: 'النتيجة',
    dash_col_date: 'التاريخ',
    dash_no_history: 'No trade history yet',
    dash_load_more: 'Load More',
    dash_loading: 'Loading...',
    dash_dur: 'Dur',
    dash_amt: 'Amt',
    dash_copying_trades: 'You are currently copying Tradiglo trades',
    dash_deposit: 'Deposit',
    dash_sell: 'Sell',
    dash_buy: 'Buy',
    dash_select_asset_hint: 'Select an asset to view chart',
    dash_select_asset_btn: 'Select Asset',
    dash_payout: 'payout',
    dash_nav_trade: 'Trade',
    dash_nav_history: 'History',
    dash_nav_copytrade: 'Copy Trade',
    dash_nav_affiliate: 'Affiliate',
    dash_nav_account: 'Account',
    dash_account_title: 'Account',
    dash_demo_balance: 'Demo Balance',
    dash_real_balance: 'Real Balance',
    dash_close_all_confirm_title: 'Close all open trades?',
    dash_close_all_confirm_desc: 'All {count} open trade{plural} will be closed at the current chart price.',
    dash_cancel: 'Cancel',
    dash_copy_following_badge: 'Following',
    dash_copy_win_rate: 'Win Rate',
    dash_copy_active_followers: 'Active Followers',
    dash_copy_min_balance: 'Min Balance',
    dash_copy_history_title: 'Copy Trade History',
    dash_copy_no_history: 'No copy trade history yet',
    dash_copy_guarantee: '100% Balance Guarantee on Loss',
    dash_copy_copying_msg: 'You are currently copying Tradiglo trades',
    dash_copy_ready_msg: 'Ready to copy Tradiglo trades?',
    dash_copy_stop_follow: 'Stop Follow',
    dash_copy_follow_btn: 'Follow Tradiglo',
    dash_copy_not_available: 'Copy Trade not available',
    dash_copy_invested: 'invested',
    dash_copy_score_activity: 'Activity',
    dash_copy_score_probability: 'Probability',
    dash_copy_score_reliability: 'Reliability',
    dash_copy_score_popularity: 'Popularity',
    dash_copy_score_experience: 'Experience',
    // Copy Trading Page
    ct_live_badge: 'نسخ التداول المباشر — مدعوم من Tradiglo',
    ct_hero_title_1: 'انسخ',
    ct_hero_title_2: 'استراتيجيتنا',
    ct_hero_subtitle: 'اتبع استراتيجية التداول الخاصة بـ Tradiglo — أعلى معدل فوز موثق في الصناعة. متاح حصريًا للأعضاء الذين لديهم رصيد لا يقل عن $',
    ct_checking_balance: 'جارٍ التحقق من الرصيد...',
    ct_activate_btn: 'เปิดใช้งานคัดลอกการเทรด',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'เริ่มต้นฟรี',
    ct_sign_in_btn: 'เข้าสู่ระบบ',
    ct_balance_qualifies: 'ยอดเงินของคุณ $',
    ct_balance_qualifies_suffix: 'ผ่านเกณฑ์ ✓',
    ct_need_more: 'ต้องการอีก $',
    ct_need_more_suffix: 'เพื่อผ่านเกณฑ์ (ขั้นต่ำ $',
    ct_stat_win_rate: 'อัตราชนะรวม',
    ct_stat_win_rate_sub: 'ยืนยัน +5 ปี',
    ct_stat_total_trades: 'จำนวนการเทรดทั้งหมด',
    ct_stat_total_trades_sub: 'มกราคม 2020 – ปัจจุบัน',
    ct_stat_total_profit: 'กำไรรวมที่สร้างได้',
    ct_stat_total_profit_sub: 'สะสม',
    ct_stat_industry: 'ค่าเฉลี่ยอุตสาหกรรม',
    ct_stat_industry_sub: 'คู่แข่ง',
    ct_guarantee_title: 'รับประกันอัตราชนะ 90%',
    ct_guarantee_sub: 'รับรองด้วยข้อมูลผลการดำเนินงานมากกว่า 5 ปี',
    ct_guarantee_desc: 'Tradiglo รับประกันอัตราชนะขั้นต่ำ <strong>90%</strong> สำหรับสัญญาณคัดลอกการเทรดทั้งหมด — สูงสุดในอุตสาหกรรม',
    ct_compare_title: 'เปรียบเทียบกับเรา',
    ct_compare_sub: 'เปรียบเทียบอัตราชนะกับมาตรฐานอุตสาหกรรม',
    ct_history_title: 'ประวัติการเทรด +5 ปี',
    ct_history_sub: 'ข้อมูลผลการดำเนินงานรายเดือนที่ผ่านการยืนยัน — 2020 年 1 月至今',
    ct_col_month: 'เดือน',
    ct_col_trades: 'การเทรด',
    ct_col_wins: 'ชนะ',
    ct_col_win_rate: 'อัตราชนะ',
    ct_col_profit: 'กำไร',
    ct_how_title: 'วิธีการทำงาน',
    ct_how_sub: 'สามขั้นตอนง่ายๆ เพื่อเริ่มคัดลอกกลยุทธ์ของเรา',
    ct_step1_title: 'สร้างบัญชี',
    ct_step1_desc: 'สมัครฟรีและยืนยันโปรไฟล์ของคุณ',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: 'เติมเงินในบัญชีจริงขั้นต่ำ $1,499 เพื่อปลดล็อกคัดลอกการเทรด',
    ct_step3_title: 'เปิดใช้งานและรับกำไร',
    ct_step3_desc: 'เปิดใช้งานคัดลอกการเทรด และอัลกอริทึมของเราจะสะท้อนทุกการเทรดให้คุณโดยอัตโนมัติ',
    ct_cta_title_1: 'พร้อมคัดลอก',
    ct_cta_title_2: 'กลยุทธ์ที่ดีที่สุดแล้วหรือยัง?',
    ct_cta_sub: 'เข้าร่วมกับสมาชิกหลายพันคนที่ได้รับประโยชน์จากระบบคัดลอกการเทรดอัตราชนะ',
    ct_cta_sub2: 'ของ Tradiglo แล้ว',
    ct_cta_start_btn: 'เริ่มคัดลอกการเทรด',
    ct_cta_go_btn: 'ไปที่แดชบอร์ดคัดลอกการเทรด',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: 'ตัวกรองคริปโต',
    sc_subtitle_prefix: 'กรอง',
    sc_subtitle_suffix: 'สกุลเงินดิจิทัลตามราคา ปริมาณ และการเคลื่อนไหว',
    sc_results: 'ผลลัพธ์',
    sc_filters_btn: 'ตัวกรอง',
    sc_reset_btn: 'รีเซ็ต',
    sc_refresh_btn: '↻ รีเฟรช',
    sc_filter_movement: 'การเคลื่อนไหว',
    sc_filter_price: 'ช่วงราคา (USD)',
    sc_filter_volume: 'ปริมาณ 24 ชม. (ล้าน)',
    sc_filter_change: 'การเปลี่ยนแปลง 24 ชม. (%)',
    sc_filter_marketcap: 'มูลค่าตลาดขั้นต่ำ (ล้าน)',
    sc_movement_all: 'ทั้งหมด',
    sc_movement_gainers: 'ขึ้น',
    sc_movement_losers: 'ลง',
    sc_error_suffix: '— API ฟรีของ CoinGecko อาจมีข้อจำกัดอัตรา กรุณาลองอีกครั้ง',
    sc_col_name: 'ชื่อ',
    sc_col_price: 'ราคา',
    sc_col_24h: '24 ชม. %',
    sc_col_high: 'สูงสุด 24 ชม.',
    sc_col_low: 'ต่ำสุด 24 ชม.',
    sc_col_volume: 'ปริมาณ',
    sc_col_marketcap: 'มูลค่าตลาด',
    sc_no_match: 'ไม่พบสกุลเงินที่ตรงกับตัวกรอง ลองปรับเกณฑ์',
    // News Page
    news_title: 'ข่าวคริปโต',
    news_subtitle: 'ข่าวสารสกุลเงินดิจิทัลและบล็อกเชนล่าสุด อัปเดตอัตโนมัติ',
    news_cat_all: 'คริปโตทั้งหมด',
    news_cat_bitcoin: 'บิตคอยน์',
    news_cat_ethereum: 'อีเธอเรียม',
    news_cat_defi: 'DeFi',
    news_showing: 'แสดงหน้า',
    news_of: 'จาก',
    news_articles: 'บทความ',
    news_read_more: 'อ่านเพิ่มเติม',
    news_no_articles: 'ไม่พบบทความสำหรับหมวดหมู่นี้',
    news_prev: '‹ ก่อนหน้า',
    news_next: 'ถัดไป ›',
    // Affiliate/Referral Page
    aff_badge: 'โปรแกรมพันธมิตร',
    aff_hero_title_1: 'รับ',
    aff_hero_title_2: 'สำหรับทุกเพื่อนที่คุณเชิญ',
    aff_hero_subtitle: 'เชิญเพื่อนมาร่วม Tradiglo และรับ 25% ของยอด depositเงินครั้งแรกทั้งหมด — ตลอดไป ไม่มีวันหมดอายุ',
    aff_join_btn: 'เข้าร่วมและเริ่มแนะนำ',
    aff_signin_btn: 'เข้าสู่ระบบแดชบอร์ด',
    aff_stat1_label: 'ค่าคอมมิชชั่นจากยอด depositครั้งแรก',
    aff_stat2_label: 'ขั้นต่ำการถอน',
    aff_stat3_label: 'ใช้ได้ตลอดชีพ',
    aff_how_title: 'วิธีการทำงาน',
    aff_how_sub: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มรับรายได้กับโปรแกรมพันธมิตร Tradiglo',
    aff_step1_title: 'สมัครและรับลิงก์ของคุณ',
    aff_step1_desc: 'สร้างบัญชี Tradiglo และรับลิงก์พันธมิตรเฉพาะจากแดชบอร์ด',
    aff_step2_title: 'เชิญเครือข่ายของคุณ',
    aff_step2_desc: 'แชร์ลิงก์พันธมิตรกับเพื่อน ครอบครัว หรือชุมชนผ่านช่องทางใดก็ได้',
    aff_step3_title: 'พวกเขาเข้าร่วมและdepositเงิน',
    aff_step3_desc: 'เมื่อมีคนสมัครผ่านลิงก์ของคุณและdepositเงินครั้งแรก การแนะนำจะถูกบันทึก',
    aff_step4_title: 'รับรางวัล 25%',
    aff_step4_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกแต่ละคนที่คุณแนะนำ',
    aff_details_title: 'รายละเอียดโปรแกรม',
    aff_details_sub: 'ทุกสิ่งที่คุณต้องรู้เกี่ยวกับโปรแกรมพันธมิตร Tradiglo',
    aff_detail1_title: 'อัตราค่าคอมมิชชั่น',
    aff_detail1_value: '25% ของยอด depositครั้งแรก',
    aff_detail1_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกทุกคนที่คุณแนะนำ',
    aff_detail2_title: 'ขั้นต่ำการถอน',
    aff_detail2_value: 'ขั้นต่ำ $500',
    aff_detail2_desc: 'รางวัลจะถูกปล่อยเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_detail3_title: 'ความถูกต้อง',
    aff_detail3_value: 'ตลอดชีพ — ไม่มีวันหมดอายุ',
    aff_detail3_desc: 'ความสัมพันธ์พันธมิตรของคุณถาวรตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_detail4_title: 'ขีดจำกัดพันธมิตร',
    aff_detail4_value: 'พันธมิตรไม่จำกัด',
    aff_detail4_desc: 'ไม่มีขีดจำกัดจำนวนคนที่คุณสามารถแนะนำได้ ยิ่งเชิญมาก ยิ่งได้มาก',
    aff_faq_title: 'คำถามที่พบบ่อย',
    aff_faq_sub: 'มีคำถาม? เรา有คำตอบ',
    aff_faq1_q: 'ฉันได้รับเท่าไหร่ต่อพันธมิตรหนึ่งคน?',
    aff_faq1_a: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกใหม่แต่ละคนที่คุณแนะนำสำเร็จ',
    aff_faq2_q: 'รางวัลจ่ายเมื่อไหร่?',
    aff_faq2_a: 'รางวัลจะจ่ายเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_faq3_q: 'โปรแกรมพันธมิตรใช้ได้นานแค่ไหน?',
    aff_faq3_a: 'โปรแกรมพันธมิตรของคุณใช้ได้ตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_faq4_q: 'คุณสามารถแนะนำคนได้มากแค่ไหน?',
    aff_faq4_a: 'ไม่มีขีดจำกัด คุณสามารถแนะนำคนได้มากเท่าที่ต้องการและรับ 25% จากยอด depositครั้งแรกของแต่ละคน',
    aff_faq5_q: 'ฉันติดตามพันธมิตรของฉันได้อย่างไร?',
    aff_faq5_a: 'เมื่อคุณเป็นสมาชิก แดชบอร์ดของคุณจะแสดงลิงก์พันธมิตร จำนวนสมาชิกที่แนะนำ และยอดรางวัลสะสม',
    aff_faq6_q: 'สมาชิกที่ถูกแนะนำได้รับประโยชน์อะไรบ้าง?',
    aff_faq6_a: 'ใช่ — สมาชิกที่ถูกแนะนำอาจได้รับโบนัสต้อนรับเมื่อเข้าร่วม ตรวจสอบโปรเจ็กต์ล่าสุดในแดชบอร์ดของคุณ',
    aff_cta_title: 'พร้อมเริ่มรับรายได้แล้วหรือยัง?',
    aff_cta_sub: 'เข้าร่วม Tradiglo วันนี้ รับลิงก์พันธมิตรเฉพาะของคุณ และเริ่มรับ 25% จากสมาชิกทุกคนที่คุณนำเข้ามา — ตลอดชีพ',
    aff_cta_btn: 'สร้างบัญชีของคุณ',
    // Markets Page
    mkt_title: 'ตลาด',
    mkt_subtitle: 'ฝึกเทรดด้วยเงินเสมือน',
    mkt_demo_trades_left: 'การเทรดทดลอง',
    mkt_demo_trades_left_plural: 'การเทรดทดลอง',
    mkt_demo_trades_suffix: 'เหลือ',
    mkt_demo_balance: 'ยอดเงินบัญชีทดลอง',
    mkt_stat_total_trades: 'การเทรดทั้งหมด',
    mkt_stat_win_rate: 'อัตราชนะ',
    mkt_stat_total_profit: 'กำไรรวม',
    mkt_stat_open_positions: 'ตำแหน่งที่เปิดอยู่',
    mkt_trading_symbol: 'สัญลักษณ์การเทรด',
    mkt_live_market: 'ตลาดสด',
    mkt_potential_profit: 'potential profit',
    mkt_payout: 'ผลตอบแทน',
    mkt_open_positions_tab: 'ตำแหน่งที่เปิดอยู่',
    mkt_history_tab: 'ประวัติ',
    mkt_no_open_positions: 'ไม่มีตำแหน่งที่เปิดอยู่ วางการเทรดเพื่อเริ่มต้น',
    mkt_no_history: 'ยังไม่มีประวัติการเทรด',
    mkt_popular_assets: 'สินทรัพย์ยอดนิยม',
    mkt_popular_assets_sub: 'ติดตามและdagangkanหุ้น สกุลเงินดิจิทัล คู่ฟอเร็กซ์ และสินค้าโภคภัณฑ์ยอดนิยม',
    mkt_entry: 'ราคาเข้า:',
    mkt_invested: 'ลงทุน',
  },
  zh: {
    hero_badge: '#1 加密货币交易平台',
    hero_heading_1: '交易加密货币',
    hero_heading_2: '像专业人士一样',
    hero_subtitle: '先进的交易工具、闪电般的执行速度和无与伦比的安全性。加入全球数百万交易者的行列。',
    hero_bonus_title: '100% 存款奖金',
    hero_bonus_desc: '存入 $1,000，立即以 $2,000 进行交易',
    hero_cta_start: '开始交易',
    hero_cta_explore: '探索市场',
    hero_feature_secure: '安全交易',
    hero_feature_realtime: '实时数据',
    hero_feature_support: '24/7 支持',
    hero_feature_deposit: '即时存款',
    hero_feature_withdraw: '即时提款（最低 $10）',
    footer_desc: 'Tradiglo 提供加密货币市场的基本面分析。除了追踪价格、交易量和市值外，Tradiglo 还追踪社区增长、开源代码开发、重大事件和链上指标。',
    footer_newsletter_title: '想要了解最新的加密货币动态吗？',
    footer_newsletter_desc: '订阅我们的免费新闻通讯，获取最新的加密货币新闻、更新和报告。',
    footer_newsletter_placeholder: '输入您的电子邮件地址',
    footer_newsletter_btn: '订阅',
    footer_copyright: '© 2026 Tradiglo. 版权所有。',
    footer_disclaimer_label: '重要免责声明：',
    footer_disclaimer_text: '本网站及相关平台提供的所有内容仅供一般信息参考，来源于第三方。我们对内容不作任何形式的保证。我们提供的任何内容均不构成财务建议或法律建议。交易是高风险活动，可能导致重大损失，请在做出任何决定前咨询您的财务顾问。',
    nav_markets: '市场',
    nav_copy_trading: '跟单交易',
    nav_screener: '筛选器',
    nav_news: '新闻',
    nav_affiliate: '推荐',
    nav_community: '社区',
    nav_dashboard: '仪表板',
    nav_sign_in: '登录',
    nav_sign_out: '退出',
    nav_get_started: '立即开始',
    footer_cat_resources: '资源',
    footer_cat_support: '支持',
    footer_cat_about: '关于 Tradiglo',
    footer_cat_community: '社区',
    markets_tab_all: '全部',
    markets_tab_highlights: '精选',
    markets_tab_categories: '分类',
    markets_tab_mobile_mining: '手机挖矿',
    markets_tab_quantum: '量子抗性',
    markets_tab_sports: '体育',
    markets_customize: '自定义',
    markets_label_all: '全部市场',
    markets_label_highlights: '精选',
    markets_label_categories: '分类',
    markets_label_mobile_mining: '手机挖矿',
    markets_label_quantum: '量子抗性',
    markets_label_sports: '体育',
    markets_coins_suffix: '种币',
    markets_refresh: '刷新',
    markets_fetching: '正在获取实时市场数据...',
    markets_try_again: '重试',
    markets_no_coins: '当前页面数据中未找到此类别的币种。',
    markets_no_coins_hint: '请尝试刷新或浏览其他页面。',
    markets_col_coin: '币种',
    markets_col_price: '价格',
    markets_col_1h: '1小时',
    markets_col_24h: '24小时',
    markets_col_7d: '7天',
    markets_col_volume: '24小时交易量',
    markets_col_marketcap: '市值',
    markets_col_chart: '近7天',
    markets_buy: '购买',
    markets_showing: '显示',
    markets_to: '至',
    markets_of: '共',
    markets_results: '条结果',
    markets_rows: '行数',
    top_movers_title: '涨跌榜',
    top_movers_live: '实时',
    crypto_label: '加密货币',
    // Dashboard
    dash_win_rate: '胜率',
    dash_balance: '余额',
    dash_profit: '净盈亏',
    dash_open: '开仓',
    dash_open_trades: '开仓交易',
    dash_close_all: '全部平仓',
    dash_open_count: '开仓',
    dash_no_open_trades: '暂无开仓交易',
    dash_col_asset: '资产',
    dash_col_type: '类型',
    dash_col_amount: '金额',
    dash_col_entry: '入场',
    dash_col_duration: '时长',
    dash_col_remaining: '剩余',
    dash_trade_history: '交易历史',
    dash_trades_count: '笔交易',
    dash_col_dir: '方向',
    dash_col_exit: '出场',
    dash_col_pl: '盈亏',
    dash_col_result: '结果',
    dash_col_date: '日期',
    dash_no_history: '暂无交易历史',
    dash_load_more: '加载更多',
    dash_loading: '加载中...',
    dash_dur: '时间',
    dash_amt: '金额',
    dash_copying_trades: '您当前正在复制 Tradiglo 交易',
    dash_deposit: 'Deposit',
    dash_sell: 'Sell',
    dash_buy: 'Buy',
    dash_select_asset_hint: 'Select an asset to view chart',
    dash_select_asset_btn: 'Select Asset',
    dash_payout: 'payout',
    dash_nav_trade: 'Trade',
    dash_nav_history: 'History',
    dash_nav_copytrade: 'Copy Trade',
    dash_nav_affiliate: 'Affiliate',
    dash_nav_account: 'Account',
    dash_account_title: 'Account',
    dash_demo_balance: 'Demo Balance',
    dash_real_balance: 'Real Balance',
    dash_close_all_confirm_title: '平仓所有开仓交易？',
    dash_close_all_confirm_desc: '所有开仓交易将按当前图表价格平仓。',
    dash_cancel: 'Cancel',
    dash_copy_following_badge: 'Following',
    dash_copy_win_rate: 'Win Rate',
    dash_copy_active_followers: 'Active Followers',
    dash_copy_min_balance: 'Min Balance',
    dash_copy_history_title: 'Copy Trade History',
    dash_copy_no_history: 'No copy trade history yet',
    dash_copy_guarantee: '100% Balance Guarantee on Loss',
    dash_copy_copying_msg: 'You are currently copying Tradiglo trades',
    dash_copy_ready_msg: 'Ready to copy Tradiglo trades?',
    dash_copy_stop_follow: 'Stop Follow',
    dash_copy_follow_btn: 'Follow Tradiglo',
    dash_copy_not_available: 'Copy Trade not available',
    dash_copy_invested: 'invested',
    dash_copy_score_activity: 'Activity',
    dash_copy_score_probability: 'Probability',
    dash_copy_score_reliability: 'Reliability',
    dash_copy_score_popularity: 'Popularity',
    dash_copy_score_experience: 'Experience',
    // Copy Trading Page
    ct_live_badge: '实时跟单交易 — 由 Tradiglo 提供支持',
    ct_hero_title_1: '复制我们的',
    ct_hero_title_2: '策略',
    ct_hero_subtitle: '跟随 Tradiglo 的专有交易策略 — 业内最高的经验证胜率。仅对最低余额为 $',
    ct_checking_balance: '正在检查余额...',
    ct_activate_btn: '激活跟单交易',
    ct_deposit_btn: '存款以解锁',
    ct_get_started_btn: '免费开始',
    ct_sign_in_btn: '登录',
    ct_balance_qualifies: '您的余额 $',
    ct_balance_qualifies_suffix: '符合条件 ✓',
    ct_need_more: '还需 $',
    ct_need_more_suffix: '才能达到资格（最低 $',
    ct_stat_win_rate: '总体胜率',
    ct_stat_win_rate_sub: '5年验证',
    ct_stat_total_trades: '总交易次数',
    ct_stat_total_trades_sub: '2020年1月 – 2025年3月',
    ct_stat_total_profit: '总利润',
    ct_stat_total_profit_sub: '累计',
    ct_stat_industry: '行业平均',
    ct_stat_industry_sub: '竞争对手',
    ct_guarantee_title: '90% 胜率保证',
    ct_guarantee_sub: '有5年经验证的业绩数据支持',
    ct_guarantee_desc: 'Tradiglo 保证所有跟单交易信号的最低 <strong>90% 胜率</strong> — 业内最高。',
    ct_compare_title: '我们的比较',
    ct_compare_sub: '与行业基准的胜率比较',
    ct_history_title: '5年交易历史',
    ct_history_sub: '经验证的月度业绩数据 — 2020年至2025年',
    ct_col_month: '月份',
    ct_col_trades: '交易次数',
    ct_col_wins: '获胜',
    ct_col_win_rate: '胜率',
    ct_col_profit: '利润',
    ct_how_title: '如何运作',
    ct_how_sub: '三个简单步骤开始复制我们的策略',
    ct_step1_title: '创建账户',
    ct_step1_desc: '免费注册并完成个人资料验证。',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: '向您的真实账户存入至少 $1,499 美元以解锁跟单交易。',
    ct_step3_title: '激活并赚取',
    ct_step3_desc: '启用跟单交易，我们的算法将自动为您镜像每笔交易。',
    ct_cta_title_1: '准备好复制',
    ct_cta_title_2: '最佳策略了吗？',
    ct_cta_sub: '加入已经从 Tradiglo',
    ct_cta_sub2: '胜率跟单交易系统中受益的数千名会员。',
    ct_cta_start_btn: '开始跟单交易',
    ct_cta_go_btn: '前往跟单交易仪表板',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: '加密货币筛选器',
    sc_subtitle_prefix: '筛选',
    sc_subtitle_suffix: '种加密货币（按价格、交易量和走势）',
    sc_results: '条结果',
    sc_filters_btn: '筛选',
    sc_reset_btn: '重置',
    sc_refresh_btn: '↻ 刷新',
    sc_filter_movement: '走势',
    sc_filter_price: '价格范围（USD）',
    sc_filter_volume: '24小时交易量（百万）',
    sc_filter_change: '24小时涨跌幅（%）',
    sc_filter_marketcap: '最低市值（百万）',
    sc_movement_all: '全部',
    sc_movement_gainers: '上涨',
    sc_movement_losers: '下跌',
    sc_error_suffix: '— CoinGecko 免费 API 可能有速率限制，请稍后重试。',
    sc_col_name: '名称',
    sc_col_price: '价格',
    sc_col_24h: '24小时 %',
    sc_col_high: '24小时最高',
    sc_col_low: '24小时最低',
    sc_col_volume: '交易量',
    sc_col_marketcap: '市值',
    sc_no_match: '没有符合筛选条件的币种，请调整条件。',
    // News Page
    news_title: '加密货币新闻',
    news_subtitle: '最新加密货币和区块链新闻，自动更新',
    news_cat_all: '全部加密',
    news_cat_bitcoin: '比特币',
    news_cat_ethereum: '以太坊',
    news_cat_defi: 'DeFi',
    news_showing: '显示第',
    news_of: '页，共',
    news_articles: '篇独特文章',
    news_read_more: '阅读更多',
    news_no_articles: '该类别未找到文章。',
    news_prev: '‹ 上一页',
    news_next: '下一页 ›',
    // Affiliate/Referral Page
    aff_badge: '推荐计划',
    aff_hero_title_1: '每邀请一位朋友',
    aff_hero_title_2: '赚取 25%',
    aff_hero_subtitle: '邀请朋友加入 Tradiglo，赚取其首次总存款的 25% — 永久有效，只要你们都是会员。',
    aff_join_btn: '加入并开始推荐',
    aff_signin_btn: '登录仪表板',
    aff_stat1_label: '首次存款佣金',
    aff_stat2_label: '最低提款门槛',
    aff_stat3_label: '终身有效',
    aff_how_title: '如何运作',
    aff_how_sub: '四个简单步骤开始赚取 Tradiglo 推荐计划收益。',
    aff_step1_title: '注册并获取链接',
    aff_step1_desc: '创建您的 Tradiglo 账户，从仪表板获取唯一推荐链接。',
    aff_step2_title: '邀请您的网络',
    aff_step2_desc: '通过任何渠道与朋友、家人或社区分享您的推荐链接。',
    aff_step3_title: '他们加入并存款',
    aff_step3_desc: '当有人通过您的链接注册并首次存款时，推荐关系即被记录。',
    aff_step4_title: '赚取 25% 奖励',
    aff_step4_desc: '您赚取每位被推荐会员首次总存款的 25%。',
    aff_details_title: '计划详情',
    aff_details_sub: '关于 Tradiglo 推荐计划您需要了解的一切。',
    aff_detail1_title: '佣金率',
    aff_detail1_value: '首次存款的 25%',
    aff_detail1_desc: '您推荐的每位会员首次存款总额的 25% 归您所有。',
    aff_detail2_title: '提款门槛',
    aff_detail2_value: '最低 $500',
    aff_detail2_desc: '当您的推荐收益累计达到 $500 时即可提款。',
    aff_detail3_title: '有效期',
    aff_detail3_value: '终身 — 永不过期',
    aff_detail3_desc: '只要您和被推荐会员都保持活跃，推荐关系就永久有效。',
    aff_detail4_title: '推荐限制',
    aff_detail4_value: '无限推荐',
    aff_detail4_desc: '您可以推荐任意数量的人，邀请越多，赚得越多。',
    aff_faq_title: '常见问题',
    aff_faq_sub: '有问题？我们有答案',
    aff_faq1_q: '每次推荐我能赚多少？',
    aff_faq1_a: '您可以赚取每位成功推荐到 Tradiglo 的新会员首次总存款的 25%。',
    aff_faq2_q: '奖励何时发放？',
    aff_faq2_a: '当您的推荐收益累计达到 $500 时即可发放。',
    aff_faq3_q: '推荐计划持续多久？',
    aff_faq3_a: '只要您和被推荐会员都是活跃的 Tradiglo 会员，推荐奖励就永久有效。',
    aff_faq4_q: '我可以推荐的人数有限制吗？',
    aff_faq4_a: '没有限制。您可以推荐任意多的人，并从每人的首次存款中赚取 25%。',
    aff_faq5_q: '如何追踪我的推荐？',
    aff_faq5_a: '成为会员后，您的仪表板将显示推荐链接、被推荐会员数量和累计奖励余额。',
    aff_faq6_q: '被推荐会员有什么好处？',
    aff_faq6_a: '是的 — 被推荐会员加入时可能会收到欢迎奖金。详情请查看仪表板上的最新促销活动。',
    aff_cta_title: '准备好开始赚取了吗？',
    aff_cta_sub: '今天加入 Tradiglo，获取您的唯一推荐链接，开始从每位您带来的会员中赚取 25% — 终身有效。',
    aff_cta_btn: '创建您的账户',
    // Markets Page
    mkt_title: '市场',
    mkt_subtitle: '使用虚拟资金练习交易',
    mkt_demo_trades_left: '次模拟交易',
    mkt_demo_trades_left_plural: '次模拟交易',
    mkt_demo_trades_suffix: '剩余',
    mkt_demo_balance: '模拟账户余额',
    mkt_stat_total_trades: '总交易次数',
    mkt_stat_win_rate: '胜率',
    mkt_stat_total_profit: '总利润',
    mkt_stat_open_positions: '开仓数量',
    mkt_trading_symbol: '交易品种',
    mkt_live_market: '实时市场',
    mkt_potential_profit: '潜在利润',
    mkt_payout: '收益率',
    mkt_open_positions_tab: '开仓',
    mkt_history_tab: '历史',
    mkt_no_open_positions: '没有开仓。下单开始交易。',
    mkt_no_history: '暂无交易历史。',
    mkt_popular_assets: '热门资产',
    mkt_popular_assets_sub: '追踪和交易最受欢迎的股票、加密货币、外汇对和大宗商品',
    mkt_entry: '入场:',
    mkt_invested: '已投资',
  },
  ms: {
    hero_badge: 'Platform Dagangan Kripto #1',
    hero_heading_1: 'Dagangan Kripto',
    hero_heading_2: 'seperti profesional',
    hero_subtitle: 'Alat dagangan canggih, pelaksanaan pantas, dan keselamatan tiada tandingan. Sertai jutaan pedagang di seluruh dunia.',
    hero_bonus_title: 'Bonus Deposit 100%',
    hero_bonus_desc: 'Deposit $1,000 dan berdagang dengan $2,000 serta-merta',
    hero_cta_start: 'Mula Berdagang',
    hero_cta_explore: 'Terokai Pasaran',
    hero_feature_secure: 'Dagangan Selamat',
    hero_feature_realtime: 'Data Masa Nyata',
    hero_feature_support: 'Sokongan 24/7',
    hero_feature_deposit: 'N_deposit Segera',
    hero_feature_withdraw: 'Pengeluaran Segera (Min $10)',
    footer_desc: 'Tradiglo menyediakan analisis asas pasaran kripto. Selain menjejaki harga, volum dan permodalan pasaran, Tradiglo menjejaki pertumbuhan komuniti, pembangunan kod sumber terbuka, peristiwa utama dan metrik on-chain.',
    footer_newsletter_title: 'Berminat untuk sentiasa dikemas kini dengan mata wang kripto?',
    footer_newsletter_desc: 'Dapatkan berita kripto terkini, kemas kini, dan laporan dengan melanggan surat berita percuma kami.',
    footer_newsletter_placeholder: 'Masukkan alamat e-mel anda',
    footer_newsletter_btn: 'Langgan',
    footer_copyright: '© 2026 Tradiglo. Hak Cipta Terpelihara.',
    footer_disclaimer_label: 'PENAFIAN PENTING:',
    footer_disclaimer_text: 'Semua kandungan yang disediakan di laman web kami dan platform berkaitan adalah untuk maklumat umum sahaja, diperolehi daripada sumber pihak ketiga. Kami tidak membuat sebarang jaminan berkaitan kandungan kami. Tiada bahagian kandungan yang kami sediakan merupakan nasihat kewangan atau undang-undang. Dagangan adalah aktiviti berisiko tinggi yang boleh membawa kepada kerugian besar, vui lòng tham khảo penasihat kewangan anda sebelum membuat sebarang keputusan.',
    nav_markets: 'Pasaran',
    nav_copy_trading: 'Salin Dagangan',
    nav_screener: 'Penapis',
    nav_news: 'Berita',
    nav_affiliate: 'Afiliasi',
    nav_community: 'Komuniti',
    nav_dashboard: 'Papan Pemuka',
    nav_sign_in: 'Log Masuk',
    nav_sign_out: 'Log Keluar',
    nav_get_started: 'Mulakan',
    footer_cat_resources: 'Sumber',
    footer_cat_support: 'Hỗ trợ',
    footer_cat_about: 'Về Tradiglo',
    footer_cat_community: 'Cộng đồng',
    markets_tab_all: 'Tất cả',
    markets_tab_highlights: 'Nổi bật',
    markets_tab_categories: 'Danh mục',
    markets_tab_mobile_mining: 'Đào di động',
    markets_tab_quantum: 'Kháng lượng tử',
    markets_tab_sports: 'Thể thao',
    markets_customize: 'Tùy chỉnh',
    markets_label_all: 'Tất cả thị trường',
    markets_label_highlights: 'Nổi bật',
    markets_label_categories: 'Danh mục',
    markets_label_mobile_mining: 'Đào di động',
    markets_label_quantum: 'Kháng lượng tử',
    markets_label_sports: 'Thể thao',
    markets_coins_suffix: 'Đồng',
    markets_refresh: 'Làm mới',
    markets_fetching: 'Đang tải dữ liệu thị trường trực tiếp...',
    markets_try_again: 'Thử lại',
    markets_no_coins: 'Không tìm thấy đồng nào trong danh mục này từ dữ liệu trang hiện tại.',
    markets_no_coins_hint: 'Hãy thử làm mới hoặc duyệt trang khác.',
    markets_col_coin: 'Đồng',
    markets_col_price: 'Giá',
    markets_col_1h: '1g',
    markets_col_24h: '24g',
    markets_col_7d: '7n',
    markets_col_volume: 'Khối lượng 24g',
    markets_col_marketcap: 'Vốn hóa',
    markets_col_chart: '7 ngày qua',
    markets_buy: 'Mua',
    markets_showing: 'Hiển thị',
    markets_to: 'đến',
    markets_of: 'trong',
    markets_results: 'kết quả',
    markets_rows: 'Hàng',
    top_movers_title: 'Biến động mạnh',
    top_movers_live: 'Trực tiếp',
    crypto_label: 'CRYPTO',
    // Dashboard
    dash_win_rate: 'Tỷ lệ thắng',
    dash_balance: 'Số dư',
    dash_profit: 'Lãi/Lỗ ròng',
    dash_open: 'Đang mở',
    dash_open_trades: 'Giao dịch đang mở',
    dash_close_all: 'Đóng tất cả',
    dash_open_count: 'đang mở',
    dash_no_open_trades: 'Không có giao dịch đang mở',
    dash_col_asset: 'Tài sản',
    dash_col_type: 'Loại',
    dash_col_amount: 'Số tiền',
    dash_col_entry: 'Vào lệnh',
    dash_col_duration: 'Thời hạn',
    dash_col_remaining: 'Còn lại',
    dash_trade_history: 'Lịch sử giao dịch',
    dash_trades_count: 'giao dịch',
    dash_col_dir: 'Hướng',
    dash_col_exit: 'Ra lệnh',
    dash_col_pl: 'L/L',
    dash_col_result: 'Kết quả',
    dash_col_date: 'Ngày',
    dash_no_history: 'Chưa có lịch sử giao dịch',
    dash_load_more: 'Tải thêm',
    dash_loading: 'Đang tải...',
    dash_dur: 'T.hạn',
    dash_amt: 'S.tiền',
    dash_copying_trades: 'Anda sedang menyalin dagangan Tradiglo',
    dash_deposit: 'Nạp tiền',
    dash_sell: 'Bán',
    dash_buy: 'Mua',
    dash_select_asset_hint: 'Chọn tài sản để xem biểu đồ',
    dash_select_asset_btn: 'Chọn tài sản',
    dash_payout: 'lợi nhuận',
    dash_nav_trade: 'Giao dịch',
    dash_nav_history: 'Lịch sử',
    dash_nav_copytrade: 'Sao chép',
    dash_nav_affiliate: 'Liên kết',
    dash_nav_account: 'Tài khoản',
    dash_account_title: 'Tài khoản',
    dash_demo_balance: 'Số dư Demo',
    dash_real_balance: 'Số dư Thực',
    dash_close_all_confirm_title: 'Đóng tất cả giao dịch đang mở?',
    dash_close_all_confirm_desc: 'Tất cả giao dịch đang mở sẽ được đóng theo giá biểu đồ hiện tại.',
    dash_cancel: 'Hủy',
    dash_copy_following_badge: 'Đang theo dõi',
    dash_copy_win_rate: 'Tỷ lệ thắng',
    dash_copy_active_followers: 'Người theo dõi',
    dash_copy_min_balance: 'Số dư tối thiểu',
    dash_copy_history_title: 'Lịch sử sao chép',
    dash_copy_no_history: 'Chưa có lịch sử sao chép',
    dash_copy_guarantee: 'Bảo đảm 100% số dư khi thua',
    dash_copy_copying_msg: 'Bạn đang sao chép giao dịch Tradiglo',
    dash_copy_ready_msg: 'Sẵn sàng sao chép giao dịch Tradiglo?',
    dash_copy_stop_follow: 'Dừng theo dõi',
    dash_copy_follow_btn: 'Theo dõi Tradiglo',
    dash_copy_not_available: 'Sao chép giao dịch không khả dụng',
    dash_copy_invested: 'đã đầu tư',
    dash_copy_score_activity: 'Hoạt động',
    dash_copy_score_probability: 'Xác suất',
    dash_copy_score_reliability: 'Độ tin cậy',
    dash_copy_score_popularity: 'Độ phổ biến',
    dash_copy_score_experience: 'Kinh nghiệm',
    // Copy Trading Page
    ct_live_badge: 'Sao chép giao dịch trực tiếp — Được cung cấp bởi Tradiglo',
    ct_hero_title_1: 'Sao chép',
    ct_hero_title_2: 'Chiến lược của chúng tôi',
    ct_hero_subtitle: 'Theo dõi chiến lược giao dịch độc quyền của Tradiglo — tỷ lệ thắng được xác minh cao nhất trong ngành. Chỉ dành cho thành viên có số dư tối thiểu $',
    ct_checking_balance: 'Đang kiểm tra số dư...',
    ct_activate_btn: 'Kích hoạt sao chép giao dịch',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'เริ่มต้นฟรี',
    ct_sign_in_btn: 'เข้าสู่ระบบ',
    ct_balance_qualifies: 'ยอดเงินของคุณ $',
    ct_balance_qualifies_suffix: 'ผ่านเกณฑ์ ✓',
    ct_need_more: 'ต้องการอีก $',
    ct_need_more_suffix: 'เพื่อผ่านเกณฑ์ (ขั้นต่ำ $',
    ct_stat_win_rate: 'Tỷ lệ thắngรวม',
    ct_stat_win_rate_sub: '5年验证',
    ct_stat_total_trades: '总交易次数',
    ct_stat_total_trades_sub: '2020年1月 – 2025年3月',
    ct_stat_total_profit: '总利润',
    ct_stat_total_profit_sub: '累计',
    ct_stat_industry: '行业平均',
    ct_stat_industry_sub: '竞争对手',
    ct_guarantee_title: '90% 胜率保证',
    ct_guarantee_sub: '有5年经验证的业绩数据支持',
    ct_guarantee_desc: 'Tradiglo 保证所有跟单交易信号的最低 <strong>90% 胜率</strong> — 业内最高。',
    ct_compare_title: '我们的比较',
    ct_compare_sub: '与行业基准的胜率比较',
    ct_history_title: '5年交易历史',
    ct_history_sub: '经验证的月度业绩数据 — 2020年至2025年',
    ct_col_month: '月份',
    ct_col_trades: '交易次数',
    ct_col_wins: '获胜',
    ct_col_win_rate: '胜率',
    ct_col_profit: '利润',
    ct_how_title: '如何运作',
    ct_how_sub: '三个简单步骤开始复制我们的策略',
    ct_step1_title: '创建账户',
    ct_step1_desc: '免费注册并完成个人资料验证。',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: '向您的真实账户存入至少 $1,499 美元以解锁跟单交易。',
    ct_step3_title: '激活并赚取',
    ct_step3_desc: '启用跟单交易，我们的算法将自动为您镜像每笔交易。',
    ct_cta_title_1: '准备好复制',
    ct_cta_title_2: '最佳策略了吗？',
    ct_cta_sub: '加入已经从 Tradiglo',
    ct_cta_sub2: '胜率跟单交易系统中受益的数千名会员。',
    ct_cta_start_btn: '开始跟单交易',
    ct_cta_go_btn: '前往跟单交易仪表板',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: 'ตัวกรอง Kripto',
    sc_subtitle_prefix: 'กรอง',
    sc_subtitle_suffix: 'สกุลเงินดิจิทัลตามราคา ปริมาณ และการเคลื่อนไหว',
    sc_results: 'keputusan',
    sc_filters_btn: 'ตัวกรอง',
    sc_reset_btn: 'Set Semula',
    sc_refresh_btn: '↻ Muat Semula',
    sc_filter_movement: 'การเคลื่อนไหว',
    sc_filter_price: 'Julat Harga (USD)',
    sc_filter_volume: 'Volum 24j (dalam Juta)',
    sc_filter_change: 'Perubahan 24j (%)',
    sc_filter_marketcap: 'Min Permodalan Pasaran (dalam Juta)',
    sc_movement_all: 'semua',
    sc_movement_gainers: 'pemenang',
    sc_movement_losers: 'penakลุก',
    sc_error_suffix: '— API percuma CoinGecko mungkin mempunyai had kadar. Sila cuba lagi sebentar.',
    sc_col_name: 'ชื่อ',
    sc_col_price: 'ราคา',
    sc_col_24h: '24 ชม. %',
    sc_col_high: 'สูงสุด 24 ชม.',
    sc_col_low: 'ต่ำสุด 24 ชม.',
    sc_col_volume: 'ปริมาณ',
    sc_col_marketcap: 'มูลค่าตลาด',
    sc_no_match: 'ไม่พบสกุลเงินที่ตรงกับตัวกรอง ลองปรับเกณฑ์',
    // News Page
    news_title: 'Berita Kripto',
    news_subtitle: 'Berita mata wang kripto dan blockchain terkini, dikemas kini secara automatik',
    news_cat_all: 'Semua Kripto',
    news_cat_bitcoin: 'Bitcoin',
    news_cat_ethereum: 'Ethereum',
    news_cat_defi: 'DeFi',
    news_showing: 'Menunjukkan halaman',
    news_of: 'daripada',
    news_articles: 'artikel unik',
    news_read_more: 'Baca lagi',
    news_no_articles: 'ไม่พบบทความสำหรับหมวดหมู่นี้',
    news_prev: '‹ Sebelum',
    news_next: 'Seterusnya ›',
    // Affiliate/Referral Page
    aff_badge: 'โปรแกรม Afiliasi',
    aff_hero_title_1: 'Kiếm',
    aff_hero_title_2: 'cho mỗiคน bạn mời',
    aff_hero_subtitle: 'Mời bạn bè tham gia Tradiglo และ kiếm 25% tổng tiền nạp lần đầuของ họ — mãi mãi,ไม่มีวันหมดอายุ',
    aff_join_btn: 'Tham gia & Bắt đầu giới thiệu',
    aff_signin_btn: 'เข้าสู่ระบบแดชบอร์ด',
    aff_stat1_label: 'Hoa hồng trênเงิน nạpครั้งแรก',
    aff_stat2_label: 'Ngưỡng thanh toán tối thiểu',
    aff_stat3_label: 'Hiệu lực trọn đời',
    aff_how_title: 'วิธีการทำงาน',
    aff_how_sub: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มรับรายได้กับโปรแกรมพันธมิตร Tradiglo',
    aff_step1_title: 'สมัครและรับลิงก์ของคุณ',
    aff_step1_desc: 'สร้างบัญชี Tradiglo และรับลิงก์พันธมิตรเฉพาะจากแดชบอร์ด',
    aff_step2_title: 'เชิญเครือข่ายของคุณ',
    aff_step2_desc: 'แชร์ลิงก์พันธมิตรกับเพื่อน ครอบครัว หรือชุมชนผ่านช่องทางใดก็ได้',
    aff_step3_title: 'Họ tham gia & Nạpเงิน',
    aff_step3_desc: 'Khi ai đó đăng kýผ่านลิงก์ของคุณและnạpเงินครั้งแรก การแนะนำจะถูกบันทึก',
    aff_step4_title: 'รับรางวัล 25%',
    aff_step4_desc: 'คุณได้รับ 25% ของยอดnạpเงินครั้งแรกทั้งหมดของสมาชิกแต่ละคนที่คุณแนะนำ',
    aff_details_title: 'รายละเอียดโปรแกรม',
    aff_details_sub: 'ทุกสิ่งที่คุณต้องรู้เกี่ยวกับโปรแกรมพันธมิตร Tradiglo',
    aff_detail1_title: 'อัตราค่าคอมมิชชั่น',
    aff_detail1_value: '25% ของยอด depositครั้งแรก',
    aff_detail1_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกทุกคนที่คุณแนะนำ',
    aff_detail2_title: 'ขั้นต่ำการถอน',
    aff_detail2_value: 'ขั้นต่ำ $500',
    aff_detail2_desc: 'รางวัลจะถูกปล่อยเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_detail3_title: 'ความถูกต้อง',
    aff_detail3_value: 'ตลอดชีพ — ไม่มีวันหมดอายุ',
    aff_detail3_desc: 'ความสัมพันธ์พันธมิตรของคุณถาวรตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_detail4_title: 'ขีดจำกัดพันธมิตร',
    aff_detail4_value: 'พันธมิตรไม่จำกัด',
    aff_detail4_desc: 'ไม่มีขีดจำกัดจำนวนคนที่คุณสามารถแนะนำได้ ยิ่งเชิญมาก ยิ่งได้มาก',
    aff_faq_title: 'คำถามที่พบบ่อย',
    aff_faq_sub: 'มีคำถาม? เรา有คำตอบ',
    aff_faq1_q: 'ฉันได้รับเท่าไหร่ต่อพันธมิตรหนึ่งคน?',
    aff_faq1_a: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกใหม่แต่ละคนที่คุณแนะนำสำเร็จ',
    aff_faq2_q: 'รางวัลจ่ายเมื่อไหร่?',
    aff_faq2_a: 'รางวัลจะจ่ายเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_faq3_q: 'โปรแกรมพันธมิตรใช้ได้นานแค่ไหน?',
    aff_faq3_a: 'โปรแกรมพันธมิตรของคุณใช้ได้ตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_faq4_q: 'คุณสามารถแนะนำคนได้มากแค่ไหน?',
    aff_faq4_a: 'ไม่มีขีดจำกัด คุณสามารถแนะนำคนได้มากเท่าที่ต้องการและรับ 25% จากยอด depositครั้งแรกของแต่ละคน',
    aff_faq5_q: 'ฉันติดตามพันธมิตรของฉันได้อย่างไร?',
    aff_faq5_a: 'เมื่อคุณเป็นสมาชิก แดชบอร์ดของคุณจะแสดงลิงก์พันธมิตร จำนวนสมาชิกที่แนะนำ และยอดรางวัลสะสม',
    aff_faq6_q: 'สมาชิกที่ถูกแนะนำได้รับประโยชน์อะไรบ้าง?',
    aff_faq6_a: 'ใช่ — สมาชิกที่ถูกแนะนำอาจได้รับโบนัสต้อนรับเมื่อเข้าร่วม ตรวจสอบโปรเจ็กต์ล่าสุดในแดชบอร์ดของคุณ',
    aff_cta_title: 'พร้อมเริ่มรับรายได้แล้วหรือยัง?',
    aff_cta_sub: 'เข้าร่วม Tradiglo วันนี้ รับลิงก์พันธมิตรเฉพาะของคุณ และเริ่มรับ 25% จากสมาชิกทุกคนที่คุณนำเข้ามา — ตลอดชีพ',
    aff_cta_btn: 'สร้างบัญชีของคุณ',
    // Markets Page
    mkt_title: 'ตลาด',
    mkt_subtitle: 'ฝึกเทรดด้วยเงินเสมือน',
    mkt_demo_trades_left: 'การเทรดทดลอง',
    mkt_demo_trades_left_plural: 'การเทรดทดลอง',
    mkt_demo_trades_suffix: 'เหลือ',
    mkt_demo_balance: 'ยอดเงินบัญชีทดลอง',
    mkt_stat_total_trades: 'การเทรดทั้งหมด',
    mkt_stat_win_rate: 'อัตราชนะ',
    mkt_stat_total_profit: 'กำไรรวม',
    mkt_stat_open_positions: 'ตำแหน่งที่เปิดอยู่',
    mkt_trading_symbol: 'สัญลักษณ์การเทรด',
    mkt_live_market: 'ตลาดสด',
    mkt_potential_profit: 'potential profit',
    mkt_payout: 'ผลตอบแทน',
    mkt_open_positions_tab: 'ตำแหน่งที่เปิดอยู่',
    mkt_history_tab: 'ประวัติ',
    mkt_no_open_positions: 'ไม่มีตำแหน่งที่เปิดอยู่ วางการเทรดเพื่อเริ่มต้น',
    mkt_no_history: 'ยังไม่มีประวัติการเทรด',
    mkt_popular_assets: 'สินทรัพย์ยอดนิยม',
    mkt_popular_assets_sub: 'ติดตามและdagangkanหุ้น สกุลเงินดิจิทัล คู่ฟอเร็กซ์ และสินค้าโภคภัณฑ์ยอดนิยม',
    mkt_entry: 'ราคาเข้า:',
    mkt_invested: 'ลงทุน',
  },
  th: {
    hero_badge: 'แพลตฟอร์มซื้อขายคริปโต #1',
    hero_heading_1: 'ซื้อขายคริปโต',
    hero_heading_2: 'อย่างมืออาชีพ',
    hero_subtitle: 'เครื่องมือการซื้อขายขั้นสูง การดำเนินการที่รวดเร็ว และความปลอดภัยที่ไม่มีใครเทียบ เข้าร่วมกับนักเทรดนับล้านทั่วโลก',
    hero_bonus_title: 'โบนัสฝากเงิน 100%',
    hero_bonus_desc: 'ฝาก $1,000 และเทรดด้วย $2,000 ทันที',
    hero_cta_start: 'เริ่มซื้อขาย',
    hero_cta_explore: 'สำรวจตลาด',
    hero_feature_secure: 'การซื้อขายที่ปลอดภัย',
    hero_feature_realtime: 'ข้อมูลเรียลไทม์',
    hero_feature_support: 'สนับสนุน 24/7',
    hero_feature_deposit: '_depositเงินทันที',
    hero_feature_withdraw: 'ถอนเงินทันที (ขั้นต่ำ $10)',
    footer_desc: 'Tradiglo ให้การวิเคราะห์พื้นฐานของตลาดคริปโต นอกจากการติดตามราคา ปริมาณ และมูลค่าตลาดแล้ว Tradiglo ยังติดตามการเติบโตของชุมชน การพัฒนาโค้ดโอเพนซอร์ส เหตุการณ์สำคัญ และเมตริกบนเชน',
    footer_newsletter_title: 'สนใจติดตามข่าวสารคริปโตล่าสุดหรือไม่?',
    footer_newsletter_desc: 'รับข่าวสารคริปโตล่าสุด อัปเดต และรายงานโดยสมัครรับจดหมายข่าวฟรีของเรา',
    footer_newsletter_placeholder: 'กรอกที่อยู่อีเมลของคุณ',
    footer_newsletter_btn: 'สมัครรับ',
    footer_copyright: '© 2026 Tradiglo. สงวนลิขสิทธิ์ทั้งหมด',
    footer_disclaimer_label: 'ข้อจำกัดความรับผิดชอบที่สำคัญ:',
    footer_disclaimer_text: 'เนื้อหาทั้งหมดที่ให้ไว้ในเว็บไซต์และแพลตฟอร์มที่เกี่ยวข้องมีไว้เพื่อข้อมูลทั่วไปเท่านั้น ได้มาจากแหล่งบุคคลที่สาม เราไม่รับประกันใดๆ เกี่ยวกับเนื้อหาของเรา ไม่มีส่วนใดของเนื้อหาที่เราให้ถือเป็นคำแนะนำทางการเงินหรือกฎ การซื้อขายเป็นกิจกรรมที่มีความเสี่ยงสูงซึ่งอาจนำไปสู่การสูญเสียครั้งใหญ่ โปรดปรึกษาที่ปรึกษาทางการเงินของคุณก่อนตัดสินใจ',
    nav_markets: 'ตลาด',
    nav_copy_trading: 'คัดลอกการซื้อขาย',
    nav_screener: 'ตัวกรอง',
    nav_news: 'ข่าว',
    nav_affiliate: 'พันธมิตร',
    nav_community: 'ชุมชน',
    nav_dashboard: 'แดชบอร์ด',
    nav_sign_in: 'เข้าสู่ระบบ',
    nav_sign_out: 'ออกจากระบบ',
    nav_get_started: 'เริ่มต้น',
    footer_cat_resources: 'ทรัพยากร',
    footer_cat_support: 'สนับสนุน',
    footer_cat_about: 'เกี่ยวกับ Tradiglo',
    footer_cat_community: 'ชุมชน',
    markets_tab_all: 'ทั้งหมด',
    markets_tab_highlights: 'เด่น',
    markets_tab_categories: 'หมวดหมู่',
    markets_tab_mobile_mining: 'ขุดบนมือถือ',
    markets_tab_quantum: 'ต้านทานควอนตัม',
    markets_tab_sports: 'กีฬา',
    markets_customize: 'ปรับแต่ง',
    markets_label_all: 'ตลาดทั้งหมด',
    markets_label_highlights: 'เด่น',
    markets_label_categories: 'หมวดหมู่',
    markets_label_mobile_mining: 'ขุดบนมือถือ',
    markets_label_quantum: 'ต้านทานควอนตัม',
    markets_label_sports: 'กีฬา',
    markets_coins_suffix: 'เหรียญ',
    markets_refresh: 'รีเฟรช',
    markets_fetching: 'กำลังดึงข้อมูลตลาดสด...',
    markets_try_again: 'ลองอีกครั้ง',
    markets_no_coins: 'ไม่พบเหรียญในหมวดหมู่นี้จากข้อมูลหน้าปัจจุบัน',
    markets_no_coins_hint: 'ลองรีเฟรชหรือเรียกดูหน้าอื่น',
    markets_col_coin: 'เหรียญ',
    markets_col_price: 'ราคา',
    markets_col_1h: '1ชม',
    markets_col_24h: '24ชม',
    markets_col_7d: '7ว',
    markets_col_volume: 'ปริมาณ 24ชม',
    markets_col_marketcap: 'มูลค่าตลาด',
    markets_col_chart: '7 วันล่าสุด',
    markets_buy: 'ซื้อ',
    markets_showing: 'แสดง',
    markets_to: 'ถึง',
    markets_of: 'จาก',
    markets_results: 'ผลลัพธ์',
    markets_rows: 'แถว',
    top_movers_title: 'ผู้นำตลาด',
    top_movers_live: 'สด',
    crypto_label: 'คริปโต',
    // Dashboard
    dash_win_rate: 'อัตราชนะ',
    dash_balance: 'ยอดคงเหลือ',
    dash_profit: 'กำไร/ขาดทุนสุทธิ',
    dash_open: 'เปิด',
    dash_open_trades: 'การเทรดที่เปิดอยู่',
    dash_close_all: 'ปิดทั้งหมด',
    dash_open_count: 'เปิด',
    dash_no_open_trades: 'ไม่มีการเทรดที่เปิดอยู่',
    dash_col_asset: 'สินทรัพย์',
    dash_col_type: 'ประเภท',
    dash_col_amount: 'จำนวน',
    dash_col_entry: 'ราคาเข้า',
    dash_col_duration: 'ระยะเวลา',
    dash_col_remaining: 'เหลือ',
    dash_trade_history: 'ประวัติการเทรด',
    dash_trades_count: 'รายการ',
    dash_col_dir: 'ทิศทาง',
    dash_col_exit: 'ราคาออก',
    dash_col_pl: '盈亏',
    dash_col_result: 'ผลลัพธ์',
    dash_col_date: 'วันที่',
    dash_no_history: 'ยังไม่มีประวัติการเทรด',
    dash_load_more: 'โหลดเพิ่มเติม',
    dash_loading: 'กำลังโหลด...',
    dash_dur: 'เวลา',
    dash_amt: 'จำนวน',
    dash_copying_trades: 'คุณกำลังคัดลอกการเทรดของ Tradiglo อยู่',
    dash_deposit: 'Nạp tiền',
    dash_sell: 'Bán',
    dash_buy: 'Mua',
    dash_select_asset_hint: 'เลือกสินทรัพย์เพื่อดูกราฟ',
    dash_select_asset_btn: 'เลือกสินทรัพย์',
    dash_payout: 'ผลตอบแทน',
    dash_nav_trade: 'เทรด',
    dash_nav_history: 'ประวัติ',
    dash_nav_copytrade: 'คัดลอก',
    dash_nav_affiliate: 'Liên kết',
    dash_nav_account: 'บัญชี',
    dash_account_title: 'บัญชี',
    dash_demo_balance: 'Số dư Demo',
    dash_real_balance: 'Số dư Thực',
    dash_close_all_confirm_title: 'ปิดการเทรดที่เปิดอยู่ทั้งหมด?',
    dash_close_all_confirm_desc: 'การเทรดที่เปิดอยู่ทั้งหมดจะถูกปิดที่ราคากราฟปัจจุบัน',
    dash_cancel: 'ยกเลิก',
    dash_copy_following_badge: 'กำลังติดตาม',
    dash_copy_win_rate: 'อัตราชนะ',
    dash_copy_active_followers: 'ผู้ติดตามที่ใช้งาน',
    dash_copy_min_balance: 'ยอดเงินขั้นต่ำ',
    dash_copy_history_title: 'ประวัติการคัดลอก',
    dash_copy_no_history: 'ยังไม่มีประวัติการคัดลอก',
    dash_copy_guarantee: '100% Balance Guarantee on Loss',
    dash_copy_copying_msg: 'คุณกำลังคัดลอกการเทรดของ Tradiglo อยู่',
    dash_copy_ready_msg: 'พร้อมคัดลอกการเทรดของ Tradiglo แล้วหรือยัง?',
    dash_copy_stop_follow: 'หยุดติดตาม',
    dash_copy_follow_btn: 'ติดตาม Tradiglo',
    dash_copy_not_available: 'ไม่มีบริการคัดลอกเทรด',
    dash_copy_invested: 'ลงทุน',
    dash_copy_score_activity: 'กิจกรรม',
    dash_copy_score_probability: 'ความน่าจะเป็น',
    dash_copy_score_reliability: 'ความน่าเชื่อถือ',
    dash_copy_score_popularity: 'ความนิยม',
    dash_copy_score_experience: 'ประสบการณ์',
    // Copy Trading Page
    ct_live_badge: 'คัดลอกการเทรดสด — ขับเคลื่อนโดย Tradiglo',
    ct_hero_title_1: 'คัดลอก',
    ct_hero_title_2: 'กลยุทธ์ของเรา',
    ct_hero_subtitle: 'ติดตามกลยุทธ์การเทรดเฉพาะของ Tradiglo — อัตราชนะที่ผ่านการยืนยันสูงสุดในอุตสาหกรรม สำหรับสมาชิกที่มียอดเงินขั้นต่ำ $',
    ct_checking_balance: 'กำลังตรวจสอบยอดเงิน...',
    ct_activate_btn: 'เปิดใช้งานคัดลอกการเทรด',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'เริ่มต้นฟรี',
    ct_sign_in_btn: 'เข้าสู่ระบบ',
    ct_balance_qualifies: 'ยอดเงินของคุณ $',
    ct_balance_qualifies_suffix: 'ผ่านเกณฑ์ ✓',
    ct_need_more: 'ต้องการอีก $',
    ct_need_more_suffix: 'เพื่อผ่านเกณฑ์ (ขั้นต่ำ $',
    ct_stat_win_rate: 'อัตราชนะรวม',
    ct_stat_win_rate_sub: 'ยืนยัน +5 ปี',
    ct_stat_total_trades: 'จำนวนการเทรดทั้งหมด',
    ct_stat_total_trades_sub: 'มกราคม 2020 – ปัจจุบัน',
    ct_stat_total_profit: 'กำไรรวมที่สร้างได้',
    ct_stat_total_profit_sub: 'สะสม',
    ct_stat_industry: 'ค่าเฉลี่ยอุตสาหกรรม',
    ct_stat_industry_sub: 'คู่แข่ง',
    ct_guarantee_title: 'รับประกันอัตราชนะ 90%',
    ct_guarantee_sub: 'รับรองด้วยข้อมูลผลการดำเนินงานมากกว่า 5 ปี',
    ct_guarantee_desc: 'Tradiglo รับประกันอัตราชนะขั้นต่ำ <strong>90%</strong> สำหรับสัญญาณคัดลอกการเทรดทั้งหมด — สูงสุดในอุตสาหกรรม',
    ct_compare_title: 'เปรียบเทียบกับเรา',
    ct_compare_sub: 'เปรียบเทียบอัตราชนะกับมาตรฐานอุตสาหกรรม',
    ct_history_title: 'ประวัติการเทรด +5 ปี',
    ct_history_sub: 'ข้อมูลผลการดำเนินงานรายเดือนที่ผ่านการยืนยัน — 2020 年 1 月至今',
    ct_col_month: 'เดือน',
    ct_col_trades: 'การเทรด',
    ct_col_wins: 'ชนะ',
    ct_col_win_rate: 'อัตราชนะ',
    ct_col_profit: 'กำไร',
    ct_how_title: 'วิธีการทำงาน',
    ct_how_sub: 'สามขั้นตอนง่ายๆ เพื่อเริ่มคัดลอกกลยุทธ์ของเรา',
    ct_step1_title: 'สร้างบัญชี',
    ct_step1_desc: 'สมัครฟรีและยืนยันโปรไฟล์ของคุณ',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: 'เติมเงินในบัญชีจริงขั้นต่ำ $1,499 เพื่อปลดล็อกคัดลอกการเทรด',
    ct_step3_title: 'เปิดใช้งานและรับกำไร',
    ct_step3_desc: 'เปิดใช้งานคัดลอกการเทรด และอัลกอริทึมของเราจะสะท้อนทุกการเทรดให้คุณโดยอัตโนมัติ',
    ct_cta_title_1: 'พร้อมคัดลอก',
    ct_cta_title_2: 'กลยุทธ์ที่ดีที่สุดแล้วหรือยัง?',
    ct_cta_sub: 'เข้าร่วมกับสมาชิกหลายพันคนที่ได้รับประโยชน์จากระบบคัดลอกการเทรดอัตราชนะ',
    ct_cta_sub2: 'ของ Tradiglo แล้ว',
    ct_cta_start_btn: 'เริ่มคัดลอกการเทรด',
    ct_cta_go_btn: 'ไปที่แดชบอร์ดคัดลอกการเทรด',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: 'ตัวกรอง Kripto',
    sc_subtitle_prefix: 'กรอง',
    sc_subtitle_suffix: 'สกุลเงินดิจิทัลตามราคา ปริมาณ และการเคลื่อนไหว',
    sc_results: 'keputusan',
    sc_filters_btn: 'ตัวกรอง',
    sc_reset_btn: 'Set Semula',
    sc_refresh_btn: '↻ Muat Semula',
    sc_filter_movement: 'การเคลื่อนไหว',
    sc_filter_price: 'Julat Harga (USD)',
    sc_filter_volume: 'Volum 24j (dalam Juta)',
    sc_filter_change: 'Perubahan 24j (%)',
    sc_filter_marketcap: 'Min Permodalan Pasaran (dalam Juta)',
    sc_movement_all: 'semua',
    sc_movement_gainers: 'pemenang',
    sc_movement_losers: 'penakลุก',
    sc_error_suffix: '— API percuma CoinGecko mungkin mempunyai had kadar. Sila cuba lagi sebentar.',
    sc_col_name: 'ชื่อ',
    sc_col_price: 'ราคา',
    sc_col_24h: '24 ชม. %',
    sc_col_high: 'สูงสุด 24 ชม.',
    sc_col_low: 'ต่ำสุด 24 ชม.',
    sc_col_volume: 'ปริมาณ',
    sc_col_marketcap: 'มูลค่าตลาด',
    sc_no_match: 'ไม่พบสกุลเงินที่ตรงกับตัวกรอง ลองปรับเกณฑ์',
    // News Page
    news_title: 'Berita Kripto',
    news_subtitle: 'Berita mata wang kripto dan blockchain terkini, dikemas kini secara automatik',
    news_cat_all: 'Semua Kripto',
    news_cat_bitcoin: 'Bitcoin',
    news_cat_ethereum: 'Ethereum',
    news_cat_defi: 'DeFi',
    news_showing: 'Menunjukkan halaman',
    news_of: 'daripada',
    news_articles: 'artikel unik',
    news_read_more: 'Baca lagi',
    news_no_articles: 'ไม่พบบทความสำหรับหมวดหมู่นี้',
    news_prev: '‹ Sebelum',
    news_next: 'Seterusnya ›',
    // Affiliate/Referral Page
    aff_badge: 'โปรแกรม Afiliasi',
    aff_hero_title_1: 'Kiếm',
    aff_hero_title_2: 'cho mỗiคน bạn mời',
    aff_hero_subtitle: 'Mời bạn bè tham gia Tradiglo และ kiếm 25% tổng tiền nạp lần đầuของ họ — mãi mãi,ไม่มีวันหมดอายุ',
    aff_join_btn: 'Tham gia & Bắt đầu giới thiệu',
    aff_signin_btn: 'เข้าสู่ระบบแดชบอร์ด',
    aff_stat1_label: 'Hoa hồng trênเงิน nạpครั้งแรก',
    aff_stat2_label: 'Ngưỡng thanh toán tối thiểu',
    aff_stat3_label: 'Hiệu lực trọn đời',
    aff_how_title: 'วิธีการทำงาน',
    aff_how_sub: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มรับรายได้กับโปรแกรมพันธมิตร Tradiglo',
    aff_step1_title: 'สมัครและรับลิงก์ของคุณ',
    aff_step1_desc: 'สร้างบัญชี Tradiglo และรับลิงก์พันธมิตรเฉพาะจากแดชบอร์ด',
    aff_step2_title: 'เชิญเครือข่ายของคุณ',
    aff_step2_desc: 'แชร์ลิงก์พันธมิตรกับเพื่อน ครอบครัว หรือชุมชนผ่านช่องทางใดก็ได้',
    aff_step3_title: 'Họ tham gia & Nạpเงิน',
    aff_step3_desc: 'Khi ai đó đăng kýผ่านลิงก์ของคุณและnạpเงินครั้งแรก การแนะนำจะถูกบันทึก',
    aff_step4_title: 'รับรางวัล 25%',
    aff_step4_desc: 'คุณได้รับ 25% ของยอดnạpเงินครั้งแรกทั้งหมดของสมาชิกแต่ละคนที่คุณแนะนำ',
    aff_details_title: 'รายละเอียดโปรแกรม',
    aff_details_sub: 'ทุกสิ่งที่คุณต้องรู้เกี่ยวกับโปรแกรมพันธมิตร Tradiglo',
    aff_detail1_title: 'อัตราค่าคอมมิชชั่น',
    aff_detail1_value: '25% ของยอด depositครั้งแรก',
    aff_detail1_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกทุกคนที่คุณแนะนำ',
    aff_detail2_title: 'ขั้นต่ำการถอน',
    aff_detail2_value: 'ขั้นต่ำ $500',
    aff_detail2_desc: 'รางวัลจะถูกปล่อยเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_detail3_title: 'ความถูกต้อง',
    aff_detail3_value: 'ตลอดชีพ — ไม่มีวันหมดอายุ',
    aff_detail3_desc: 'ความสัมพันธ์พันธมิตรของคุณถาวรตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_detail4_title: 'ขีดจำกัดพันธมิตร',
    aff_detail4_value: 'พันธมิตรไม่จำกัด',
    aff_detail4_desc: 'ไม่มีขีดจำกัดจำนวนคนที่คุณสามารถแนะนำได้ ยิ่งเชิญมาก ยิ่งได้มาก',
    aff_faq_title: 'คำถามที่พบบ่อย',
    aff_faq_sub: 'มีคำถาม? เรา有คำตอบ',
    aff_faq1_q: 'ฉันได้รับเท่าไหร่ต่อพันธมิตรหนึ่งคน?',
    aff_faq1_a: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกใหม่แต่ละคนที่คุณแนะนำสำเร็จ',
    aff_faq2_q: 'รางวัลจ่ายเมื่อไหร่?',
    aff_faq2_a: 'รางวัลจะจ่ายเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_faq3_q: 'โปรแกรมพันธมิตรใช้ได้นานแค่ไหน?',
    aff_faq3_a: 'โปรแกรมพันธมิตรของคุณใช้ได้ตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_faq4_q: 'คุณสามารถแนะนำคนได้มากแค่ไหน?',
    aff_faq4_a: 'ไม่มีขีดจำกัด คุณสามารถแนะนำคนได้มากเท่าที่ต้องการและรับ 25% จากยอด depositครั้งแรกของแต่ละคน',
    aff_faq5_q: 'ฉันติดตามพันธมิตรของฉันได้อย่างไร?',
    aff_faq5_a: 'เมื่อคุณเป็นสมาชิก แดชบอร์ดของคุณจะแสดงลิงก์พันธมิตร จำนวนสมาชิกที่แนะนำ และยอดรางวัลสะสม',
    aff_faq6_q: 'สมาชิกที่ถูกแนะนำได้รับประโยชน์อะไรบ้าง?',
    aff_faq6_a: 'ใช่ — สมาชิกที่ถูกแนะนำอาจได้รับโบนัสต้อนรับเมื่อเข้าร่วม ตรวจสอบโปรเจ็กต์ล่าสุดในแดชบอร์ดของคุณ',
    aff_cta_title: 'พร้อมเริ่มรับรายได้แล้วหรือยัง?',
    aff_cta_sub: 'เข้าร่วม Tradiglo วันนี้ รับลิงก์พันธมิตรเฉพาะของคุณ และเริ่มรับ 25% จากสมาชิกทุกคนที่คุณนำเข้ามา — ตลอดชีพ',
    aff_cta_btn: 'สร้างบัญชีของคุณ',
    // Markets Page
    mkt_title: 'ตลาด',
    mkt_subtitle: 'ฝึกเทรดด้วยเงินเสมือน',
    mkt_demo_trades_left: 'การเทรดทดลอง',
    mkt_demo_trades_left_plural: 'การเทรดทดลอง',
    mkt_demo_trades_suffix: 'เหลือ',
    mkt_demo_balance: 'ยอดเงินบัญชีทดลอง',
    mkt_stat_total_trades: 'การเทรดทั้งหมด',
    mkt_stat_win_rate: 'อัตราชนะ',
    mkt_stat_total_profit: 'กำไรรวม',
    mkt_stat_open_positions: 'ตำแหน่งที่เปิดอยู่',
    mkt_trading_symbol: 'สัญลักษณ์การเทรด',
    mkt_live_market: 'ตลาดสด',
    mkt_potential_profit: 'potential profit',
    mkt_payout: 'ผลตอบแทน',
    mkt_open_positions_tab: 'ตำแหน่งที่เปิดอยู่',
    mkt_history_tab: 'ประวัติ',
    mkt_no_open_positions: 'ไม่มีตำแหน่งที่เปิดอยู่ วางการเทรดเพื่อเริ่มต้น',
    mkt_no_history: 'ยังไม่มีประวัติการเทรด',
    mkt_popular_assets: 'สินทรัพย์ยอดนิยม',
    mkt_popular_assets_sub: 'ติดตามและdagangkanหุ้น สกุลเงินดิจิทัล คู่ฟอเร็กซ์ และสินค้าโภคภัณฑ์ยอดนิยม',
    mkt_entry: 'ราคาเข้า:',
    mkt_invested: 'ลงทุน',
  },
  th: {
    hero_badge: 'แพลตฟอร์มซื้อขายคริปโต #1',
    hero_heading_1: 'ซื้อขายคริปโต',
    hero_heading_2: 'อย่างมืออาชีพ',
    hero_subtitle: 'เครื่องมือการซื้อขายขั้นสูง การดำเนินการที่รวดเร็ว และความปลอดภัยที่ไม่มีใครเทียบ เข้าร่วมกับนักเทรดนับล้านทั่วโลก',
    hero_bonus_title: 'โบนัสฝากเงิน 100%',
    hero_bonus_desc: 'ฝาก $1,000 และเทรดด้วย $2,000 ทันที',
    hero_cta_start: 'เริ่มซื้อขาย',
    hero_cta_explore: 'สำรวจตลาด',
    hero_feature_secure: 'การซื้อขายที่ปลอดภัย',
    hero_feature_realtime: 'ข้อมูลเรียลไทม์',
    hero_feature_support: 'สนับสนุน 24/7',
    hero_feature_deposit: '_depositเงินทันที',
    hero_feature_withdraw: 'ถอนเงินทันที (ขั้นต่ำ $10)',
    footer_desc: 'Tradiglo ให้การวิเคราะห์พื้นฐานของตลาดคริปโต นอกจากการติดตามราคา ปริมาณ และมูลค่าตลาดแล้ว Tradiglo ยังติดตามการเติบโตของชุมชน การพัฒนาโค้ดโอเพนซอร์ส เหตุการณ์สำคัญ และเมตริกบนเชน',
    footer_newsletter_title: 'สนใจติดตามข่าวสารคริปโตล่าสุดหรือไม่?',
    footer_newsletter_desc: 'รับข่าวสารคริปโตล่าสุด อัปเดต และรายงานโดยสมัครรับจดหมายข่าวฟรีของเรา',
    footer_newsletter_placeholder: 'กรอกที่อยู่อีเมลของคุณ',
    footer_newsletter_btn: 'สมัครรับ',
    footer_copyright: '© 2026 Tradiglo. สงวนลิขสิทธิ์ทั้งหมด',
    footer_disclaimer_label: 'ข้อจำกัดความรับผิดชอบที่สำคัญ:',
    footer_disclaimer_text: 'เนื้อหาทั้งหมดที่ให้ไว้ในเว็บไซต์และแพลตฟอร์มที่เกี่ยวข้องมีไว้เพื่อข้อมูลทั่วไปเท่านั้น ได้มาจากแหล่งบุคคลที่สาม เราไม่รับประกันใดๆ เกี่ยวกับเนื้อหาของเรา ไม่มีส่วนใดของเนื้อหาที่เราให้ถือเป็นคำแนะนำทางการเงินหรือกฎ การซื้อขายเป็นกิจกรรมที่มีความเสี่ยงสูงซึ่งอาจนำไปสู่การสูญเสียครั้งใหญ่ โปรดปรึกษาที่ปรึกษาทางการเงินของคุณก่อนตัดสินใจ',
    nav_markets: 'ตลาด',
    nav_copy_trading: 'คัดลอกการซื้อขาย',
    nav_screener: 'ตัวกรอง',
    nav_news: 'ข่าว',
    nav_affiliate: 'พันธมิตร',
    nav_dashboard: 'แดชบอร์ด',
    nav_sign_in: 'เข้าสู่ระบบ',
    nav_sign_out: 'ออกจากระบบ',
    nav_get_started: 'เริ่มต้น',
    footer_cat_resources: 'ทรัพยากร',
    footer_cat_support: 'สนับสนุน',
    footer_cat_about: 'เกี่ยวกับ Tradiglo',
    footer_cat_community: 'ชุมชน',
    markets_tab_all: 'ทั้งหมด',
    markets_tab_highlights: 'เด่น',
    markets_tab_categories: 'หมวดหมู่',
    markets_tab_mobile_mining: 'ขุดบนมือถือ',
    markets_tab_quantum: 'ต้านทานควอนตัม',
    markets_tab_sports: 'กีฬา',
    markets_customize: 'ปรับแต่ง',
    markets_label_all: 'ตลาดทั้งหมด',
    markets_label_highlights: 'เด่น',
    markets_label_categories: 'หมวดหมู่',
    markets_label_mobile_mining: 'ขุดบนมือถือ',
    markets_label_quantum: 'ต้านทานควอนตัม',
    markets_label_sports: 'กีฬา',
    markets_coins_suffix: 'เหรียญ',
    markets_refresh: 'รีเฟรช',
    markets_fetching: 'กำลังดึงข้อมูลตลาดสด...',
    markets_try_again: 'ลองอีกครั้ง',
    markets_no_coins: 'ไม่พบเหรียญในหมวดหมู่นี้จากข้อมูลหน้าปัจจุบัน',
    markets_no_coins_hint: 'ลองรีเฟรชหรือเรียกดูหน้าอื่น',
    markets_col_coin: 'เหรียญ',
    markets_col_price: 'ราคา',
    markets_col_1h: '1ชม',
    markets_col_24h: '24ชม',
    markets_col_7d: '7ว',
    markets_col_volume: 'ปริมาณ 24ชม',
    markets_col_marketcap: 'มูลค่าตลาด',
    markets_col_chart: '7 วันล่าสุด',
    markets_buy: 'ซื้อ',
    markets_showing: 'แสดง',
    markets_to: 'ถึง',
    markets_of: 'จาก',
    markets_results: 'ผลลัพธ์',
    markets_rows: 'แถว',
    top_movers_title: 'ผู้นำตลาด',
    top_movers_live: 'สด',
    crypto_label: 'คริปโต',
    // Dashboard
    dash_win_rate: 'อัตราชนะ',
    dash_balance: 'ยอดคงเหลือ',
    dash_profit: 'กำไร/ขาดทุนสุทธิ',
    dash_open: 'เปิด',
    dash_open_trades: 'การเทรดที่เปิดอยู่',
    dash_close_all: 'ปิดทั้งหมด',
    dash_open_count: 'เปิด',
    dash_no_open_trades: 'ไม่มีการเทรดที่เปิดอยู่',
    dash_col_asset: 'สินทรัพย์',
    dash_col_type: 'ประเภท',
    dash_col_amount: 'จำนวน',
    dash_col_entry: 'ราคาเข้า',
    dash_col_duration: 'ระยะเวลา',
    dash_col_remaining: 'เหลือ',
    dash_trade_history: 'ประวัติการเทรด',
    dash_trades_count: 'รายการ',
    dash_col_dir: 'ทิศทาง',
    dash_col_exit: 'ราคาออก',
    dash_col_pl: '盈亏',
    dash_col_result: 'ผลลัพธ์',
    dash_col_date: 'วันที่',
    dash_no_history: 'ยังไม่มีประวัติการเทรด',
    dash_load_more: 'โหลดเพิ่มเติม',
    dash_loading: 'กำลังโหลด...',
    dash_dur: 'เวลา',
    dash_amt: 'จำนวน',
    dash_copying_trades: 'คุณกำลังคัดลอกการเทรดของ Tradiglo อยู่',
    dash_deposit: 'Nạp tiền',
    dash_sell: 'Bán',
    dash_buy: 'Mua',
    dash_select_asset_hint: 'เลือกสินทรัพย์เพื่อดูกราฟ',
    dash_select_asset_btn: 'เลือกสินทรัพย์',
    dash_payout: 'ผลตอบแทน',
    dash_nav_trade: 'Giao dịch',
    dash_nav_history: 'Lịch sử',
    dash_nav_copytrade: 'คัดลอก',
    dash_nav_affiliate: 'Liên kết',
    dash_nav_account: 'บัญชี',
    dash_account_title: 'บัญชี',
    dash_demo_balance: 'Số dư Demo',
    dash_real_balance: 'Số dư Thực',
    dash_close_all_confirm_title: 'ปิดการเทรดที่เปิดอยู่ทั้งหมด?',
    dash_close_all_confirm_desc: 'การเทรดที่เปิดอยู่ทั้งหมดจะถูกปิดที่ราคากราฟปัจจุบัน',
    dash_cancel: 'ยกเลิก',
    dash_copy_following_badge: 'กำลังติดตาม',
    dash_copy_win_rate: 'อัตราชนะ',
    dash_copy_active_followers: 'ผู้ติดตามที่ใช้งาน',
    dash_copy_min_balance: 'ยอดเงินขั้นต่ำ',
    dash_copy_history_title: 'ประวัติการคัดลอก',
    dash_copy_no_history: 'ยังไม่มีประวัติการคัดลอก',
    dash_copy_guarantee: '100% Balance Guarantee on Loss',
    dash_copy_copying_msg: 'คุณกำลังคัดลอกการเทรดของ Tradiglo อยู่',
    dash_copy_ready_msg: 'พร้อมคัดลอกการเทรดของ Tradiglo แล้วหรือยัง?',
    dash_copy_stop_follow: 'หยุดติดตาม',
    dash_copy_follow_btn: 'ติดตาม Tradiglo',
    dash_copy_not_available: 'ไม่มีบริการคัดลอกเทรด',
    dash_copy_invested: 'ลงทุน',
    dash_copy_score_activity: 'กิจกรรม',
    dash_copy_score_probability: 'ความน่าจะเป็น',
    dash_copy_score_reliability: 'ความน่าเชื่อถือ',
    dash_copy_score_popularity: 'ความนิยม',
    dash_copy_score_experience: 'ประสบการณ์',
    // Copy Trading Page
    ct_live_badge: 'คัดลอกการเทรดสด — ขับเคลื่อนโดย Tradiglo',
    ct_hero_title_1: 'คัดลอก',
    ct_hero_title_2: 'กลยุทธ์ของเรา',
    ct_hero_subtitle: 'ติดตามกลยุทธ์การเทรดเฉพาะของ Tradiglo — อัตราชนะที่ผ่านการยืนยันสูงสุดในอุตสาหกรรม สำหรับสมาชิกที่มียอดเงินขั้นต่ำ $',
    ct_checking_balance: 'กำลังตรวจสอบยอดเงิน...',
    ct_activate_btn: 'เปิดใช้งานคัดลอกการเทรด',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'เริ่มต้นฟรี',
    ct_sign_in_btn: 'เข้าสู่ระบบ',
    ct_balance_qualifies: 'ยอดเงินของคุณ $',
    ct_balance_qualifies_suffix: 'ผ่านเกณฑ์ ✓',
    ct_need_more: 'ต้องการอีก $',
    ct_need_more_suffix: 'เพื่อผ่านเกณฑ์ (ขั้นต่ำ $',
    ct_stat_win_rate: 'อัตราชนะรวม',
    ct_stat_win_rate_sub: 'ยืนยัน +5 ปี',
    ct_stat_total_trades: 'จำนวนการเทรดทั้งหมด',
    ct_stat_total_trades_sub: 'มกราคม 2020 – ปัจจุบัน',
    ct_stat_total_profit: 'กำไรรวมที่สร้างได้',
    ct_stat_total_profit_sub: 'สะสม',
    ct_stat_industry: 'ค่าเฉลี่ยอุตสาหกรรม',
    ct_stat_industry_sub: 'คู่แข่ง',
    ct_guarantee_title: 'รับประกันอัตราชนะ 90%',
    ct_guarantee_sub: 'รับรองด้วยข้อมูลผลการดำเนินงานมากกว่า 5 ปี',
    ct_guarantee_desc: 'Tradiglo รับประกันอัตราชนะขั้นต่ำ <strong>90%</strong> สำหรับสัญญาณคัดลอกการเทรดทั้งหมด — สูงสุดในอุตสาหกรรม',
    ct_compare_title: 'เปรียบเทียบกับเรา',
    ct_compare_sub: 'เปรียบเทียบอัตราชนะกับมาตรฐานอุตสาหกรรม',
    ct_history_title: 'ประวัติการเทรด +5 ปี',
    ct_history_sub: 'ข้อมูลผลการดำเนินงานรายเดือนที่ผ่านการยืนยัน — 2020 年 1 月至今',
    ct_col_month: 'เดือน',
    ct_col_trades: 'การเทรด',
    ct_col_wins: 'ชนะ',
    ct_col_win_rate: 'อัตราชนะ',
    ct_col_profit: 'กำไร',
    ct_how_title: 'วิธีการทำงาน',
    ct_how_sub: 'สามขั้นตอนง่ายๆ เพื่อเริ่มคัดลอกกลยุทธ์ของเรา',
    ct_step1_title: 'สร้างบัญชี',
    ct_step1_desc: 'สมัครฟรีและยืนยันโปรไฟล์ของคุณ',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: 'เติมเงินในบัญชีจริงขั้นต่ำ $1,499 เพื่อปลดล็อกคัดลอกการเทรด',
    ct_step3_title: 'เปิดใช้งานและรับกำไร',
    ct_step3_desc: 'เปิดใช้งานคัดลอกการเทรด และอัลกอริทึมของเราจะสะท้อนทุกการเทรดให้คุณโดยอัตโนมัติ',
    ct_cta_title_1: 'พร้อมคัดลอก',
    ct_cta_title_2: 'กลยุทธ์ที่ดีที่สุดแล้วหรือยัง?',
    ct_cta_sub: 'เข้าร่วมกับสมาชิกหลายพันคนที่ได้รับประโยชน์จากระบบคัดลอกการเทรดอัตราชนะ',
    ct_cta_sub2: 'ของ Tradiglo แล้ว',
    ct_cta_start_btn: 'เริ่มคัดลอกการเทรด',
    ct_cta_go_btn: 'ไปที่แดชบอร์ดคัดลอกการเทรด',
    ct_cta_deposit_btn: 'Deposit Now to Unlock',
    // Screener Page
    sc_title: 'ตัวกรอง Kripto',
    sc_subtitle_prefix: 'กรอง',
    sc_subtitle_suffix: 'สกุลเงินดิจิทัลตามราคา ปริมาณ และการเคลื่อนไหว',
    sc_results: 'keputusan',
    sc_filters_btn: 'ตัวกรอง',
    sc_reset_btn: 'Set Semula',
    sc_refresh_btn: '↻ Muat Semula',
    sc_filter_movement: 'การเคลื่อนไหว',
    sc_filter_price: 'Julat Harga (USD)',
    sc_filter_volume: 'Volum 24j (dalam Juta)',
    sc_filter_change: 'Perubahan 24j (%)',
    sc_filter_marketcap: 'Min Permodalan Pasaran (dalam Juta)',
    sc_movement_all: 'semua',
    sc_movement_gainers: 'pemenang',
    sc_movement_losers: 'penakลุก',
    sc_error_suffix: '— API percuma CoinGecko mungkin mempunyai had kadar. Sila cuba lagi sebentar.',
    sc_col_name: 'ชื่อ',
    sc_col_price: 'ราคา',
    sc_col_24h: '24 ชม. %',
    sc_col_high: 'สูงสุด 24 ชม.',
    sc_col_low: 'ต่ำสุด 24 ชม.',
    sc_col_volume: 'ปริมาณ',
    sc_col_marketcap: 'มูลค่าตลาด',
    sc_no_match: 'ไม่พบสกุลเงินที่ตรงกับตัวกรอง ลองปรับเกณฑ์',
    // News Page
    news_title: 'Berita Kripto',
    news_subtitle: 'Berita mata wang kripto dan blockchain terkini, dikemas kini secara automatik',
    news_cat_all: 'Semua Kripto',
    news_cat_bitcoin: 'Bitcoin',
    news_cat_ethereum: 'Ethereum',
    news_cat_defi: 'DeFi',
    news_showing: 'Menunjukkan halaman',
    news_of: 'daripada',
    news_articles: 'artikel unik',
    news_read_more: 'Baca lagi',
    news_no_articles: 'ไม่พบบทความสำหรับหมวดหมู่นี้',
    news_prev: '‹ Sebelum',
    news_next: 'Seterusnya ›',
    // Affiliate/Referral Page
    aff_badge: 'โปรแกรม Afiliasi',
    aff_hero_title_1: 'Kiếm',
    aff_hero_title_2: 'cho mỗiคน bạn mời',
    aff_hero_subtitle: 'Mời bạn bè tham gia Tradiglo และ kiếm 25% tổng tiền nạp lần đầuของ họ — mãi mãi,ไม่มีวันหมดอายุ',
    aff_join_btn: 'Tham gia & Bắt đầu giới thiệu',
    aff_signin_btn: 'เข้าสู่ระบบแดชบอร์ด',
    aff_stat1_label: 'Hoa hồng trênเงิน nạpครั้งแรก',
    aff_stat2_label: 'Ngưỡng thanh toán tối thiểu',
    aff_stat3_label: 'Hiệu lực trọn đời',
    aff_how_title: 'วิธีการทำงาน',
    aff_how_sub: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มรับรายได้กับโปรแกรมพันธมิตร Tradiglo',
    aff_step1_title: 'สมัครและรับลิงก์ของคุณ',
    aff_step1_desc: 'สร้างบัญชี Tradiglo และรับลิงก์พันธมิตรเฉพาะจากแดชบอร์ด',
    aff_step2_title: 'เชิญเครือข่ายของคุณ',
    aff_step2_desc: 'แชร์ลิงก์พันธมิตรกับเพื่อน ครอบครัว หรือชุมชนผ่านช่องทางใดก็ได้',
    aff_step3_title: 'Họ tham gia & Nạpเงิน',
    aff_step3_desc: 'Khi ai đó đăng kýผ่านลิงก์ของคุณและnạpเงินครั้งแรก การแนะนำจะถูกบันทึก',
    aff_step4_title: 'รับรางวัล 25%',
    aff_step4_desc: 'คุณได้รับ 25% ของยอดnạpเงินครั้งแรกทั้งหมดของสมาชิกแต่ละคนที่คุณแนะนำ',
    aff_details_title: 'รายละเอียดโปรแกรม',
    aff_details_sub: 'ทุกสิ่งที่คุณต้องรู้เกี่ยวกับโปรแกรมพันธมิตร Tradiglo',
    aff_detail1_title: 'อัตราค่าคอมมิชชั่น',
    aff_detail1_value: '25% ของยอด depositครั้งแรก',
    aff_detail1_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกทุกคนที่คุณแนะนำ',
    aff_detail2_title: 'ขั้นต่ำการถอน',
    aff_detail2_value: 'ขั้นต่ำ $500',
    aff_detail2_desc: 'รางวัลจะถูกปล่อยเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_detail3_title: 'ความถูกต้อง',
    aff_detail3_value: 'ตลอดชีพ — ไม่มีวันหมดอายุ',
    aff_detail3_desc: 'ความสัมพันธ์พันธมิตรของคุณถาวรตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_detail4_title: 'ขีดจำกัดพันธมิตร',
    aff_detail4_value: 'พันธมิตรไม่จำกัด',
    aff_detail4_desc: 'ไม่มีขีดจำกัดจำนวนคนที่คุณสามารถแนะนำได้ ยิ่งเชิญมาก ยิ่งได้มาก',
    aff_faq_title: 'คำถามที่พบบ่อย',
    aff_faq_sub: 'มีคำถาม? เรา有คำตอบ',
    aff_faq1_q: 'ฉันได้รับเท่าไหร่ต่อพันธมิตรหนึ่งคน?',
    aff_faq1_a: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกใหม่แต่ละคนที่คุณแนะนำสำเร็จ',
    aff_faq2_q: 'รางวัลจ่ายเมื่อไหร่?',
    aff_faq2_a: 'รางวัลจะจ่ายเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_faq3_q: 'โปรแกรมพันธมิตรใช้ได้นานแค่ไหน?',
    aff_faq3_a: 'โปรแกรมพันธมิตรของคุณใช้ได้ตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_faq4_q: 'คุณสามารถแนะนำคนได้มากแค่ไหน?',
    aff_faq4_a: 'ไม่มีขีดจำกัด คุณสามารถแนะนำคนได้มากเท่าที่ต้องการและรับ 25% จากยอด depositครั้งแรกของแต่ละคน',
    aff_faq5_q: 'ฉันติดตามพันธมิตรของฉันได้อย่างไร?',
    aff_faq5_a: 'เมื่อคุณเป็นสมาชิก แดชบอร์ดของคุณจะแสดงลิงก์พันธมิตร จำนวนสมาชิกที่แนะนำ และยอดรางวัลสะสม',
    aff_faq6_q: 'สมาชิกที่ถูกแนะนำได้รับประโยชน์อะไรบ้าง?',
    aff_faq6_a: 'ใช่ — สมาชิกที่ถูกแนะนำอาจได้รับโบนัสต้อนรับเมื่อเข้าร่วม ตรวจสอบโปรเจ็กต์ล่าสุดในแดชบอร์ดของคุณ',
    aff_cta_title: 'พร้อมเริ่มรับรายได้แล้วหรือยัง?',
    aff_cta_sub: 'เข้าร่วม Tradiglo วันนี้ รับลิงก์พันธมิตรเฉพาะของคุณ และเริ่มรับ 25% จากสมาชิกทุกคนที่คุณนำเข้ามา — ตลอดชีพ',
    aff_cta_btn: 'สร้างบัญชีของคุณ',
    // Markets Page
    mkt_title: 'ตลาด',
    mkt_subtitle: 'ฝึกเทรดด้วยเงินเสมือน',
    mkt_demo_trades_left: 'การเทรดทดลอง',
    mkt_demo_trades_left_plural: 'การเทรดทดลอง',
    mkt_demo_trades_suffix: 'เหลือ',
    mkt_demo_balance: 'ยอดเงินบัญชีทดลอง',
    mkt_stat_total_trades: 'การเทรดทั้งหมด',
    mkt_stat_win_rate: 'อัตราชนะ',
    mkt_stat_total_profit: 'กำไรรวม',
    mkt_stat_open_positions: 'ตำแหน่งที่เปิดอยู่',
    mkt_trading_symbol: 'สัญลักษณ์การเทรด',
    mkt_live_market: 'ตลาดสด',
    mkt_potential_profit: 'potential profit',
    mkt_payout: 'ผลตอบแทน',
    mkt_open_positions_tab: 'ตำแหน่งที่เปิดอยู่',
    mkt_history_tab: 'ประวัติ',
    mkt_no_open_positions: 'ไม่มีตำแหน่งที่เปิดอยู่ วางการเทรดเพื่อเริ่มต้น',
    mkt_no_history: 'ยังไม่มีประวัติการเทรด',
    mkt_popular_assets: 'สินทรัพย์ยอดนิยม',
    mkt_popular_assets_sub: 'ติดตามและdagangkanหุ้น สกุลเงินดิจิทัล คู่ฟอเร็กซ์ และสินค้าโภคภัณฑ์ยอดนิยม',
    mkt_entry: 'ราคาเข้า:',
    mkt_invested: 'ลงทุน',
  },
  vi: {
    hero_badge: 'Nền tảng giao dịch Crypto #1',
    hero_heading_1: 'Giao dịch Crypto',
    hero_heading_2: 'như chuyên gia',
    hero_subtitle: 'Công cụ giao dịch tiên tiến, thực thi nhanh như chớp và bảo mật vô song. Tham gia cùng hàng triệu nhà giao dịch trên toàn thế giới.',
    hero_bonus_title: 'Thưởng nạp tiền 100%',
    hero_bonus_desc: 'Nạp $1,000 và giao dịch với $2,000 ngay lập tức',
    hero_cta_start: 'Bắt đầu giao dịch',
    hero_cta_explore: 'Khám phá thị trường',
    hero_feature_secure: 'Giao dịch an toàn',
    hero_feature_realtime: 'Dữ liệu thời gian thực',
    hero_feature_support: 'Hỗ trợ 24/7',
    hero_feature_deposit: 'Nạp tiền tức thì',
    hero_feature_withdraw: 'Rút tiền tức thì (Tối thiểu $10)',
    footer_desc: 'Tradiglo cung cấp phân tích cơ bản về thị trường tiền điện tử. Ngoài việc theo dõi giá, khối lượng và vốn hóa thị trường, Tradiglo còn theo dõi sự tăng trưởng cộng đồng, phát triển mã nguồn mở, các sự kiện lớn và các chỉ số on-chain.',
    footer_newsletter_title: 'Bạn có muốn cập nhật thông tin về tiền điện tử không?',
    footer_newsletter_desc: 'Nhận tin tức, cập nhật và báo cáo tiền điện tử mới nhất bằng cách đăng ký bản tin miễn phí của chúng tôi.',
    footer_newsletter_placeholder: 'Nhập địa chỉ email của bạn',
    footer_newsletter_btn: 'Đăng ký',
    footer_copyright: '© 2026 Tradiglo. Bảo lưu mọi quyền.',
    footer_disclaimer_label: 'TUYÊN BỐ MIỄN TRỪ TRÁCH NHIỆM QUAN TRỌNG:',
    footer_disclaimer_text: 'Tất cả nội dung được cung cấp trên trang web và các nền tảng liên quan của chúng tôi chỉ dành cho mục đích thông tin chung, được lấy từ các nguồn bên thứ ba. Chúng tôi không đưa ra bất kỳ bảo đảm nào liên quan đến nội dung của mình. Không có phần nào trong nội dung chúng tôi cung cấp cấu thành lời khuyên tài chính hoặc pháp lý. Giao dịch là hoạt động có rủi ro cao có thể dẫn đến tổn thất lớn, vui lòng tham khảo cố vấn tài chính của bạn trước khi đưa ra bất kỳ quyết định nào.',
    nav_markets: 'Thị trường',
    nav_copy_trading: 'Sao chép giao dịch',
    nav_screener: 'Bộ lọc',
    nav_news: 'Tin tức',
    nav_affiliate: 'Liên kết',
    nav_community: 'Cộng đồng',
    nav_dashboard: 'Bảng điều khiển',
    nav_sign_in: 'Đăng nhập',
    nav_sign_out: 'Đăng xuất',
    nav_get_started: 'Bắt đầu',
    footer_cat_resources: 'Tài nguyên',
    footer_cat_support: 'Hỗ trợ',
    footer_cat_about: 'Về Tradiglo',
    footer_cat_community: 'Cộng đồng',
    markets_tab_all: 'Tất cả',
    markets_tab_highlights: 'Nổi bật',
    markets_tab_categories: 'Danh mục',
    markets_tab_mobile_mining: 'Đào di động',
    markets_tab_quantum: 'Kháng lượng tử',
    markets_tab_sports: 'Thể thao',
    markets_customize: 'Tùy chỉnh',
    markets_label_all: 'Tất cả thị trường',
    markets_label_highlights: 'Nổi bật',
    markets_label_categories: 'Danh mục',
    markets_label_mobile_mining: 'Đào di động',
    markets_label_quantum: 'Kháng lượng tử',
    markets_label_sports: 'Thể thao',
    markets_coins_suffix: 'Đồng',
    markets_refresh: 'Làm mới',
    markets_fetching: 'Đang tải dữ liệu thị trường trực tiếp...',
    markets_try_again: 'Thử lại',
    markets_no_coins: 'Không tìm thấy đồng nào trong danh mục này từ dữ liệu trang hiện tại.',
    markets_no_coins_hint: 'Hãy thử làm mới hoặc duyệt trang khác.',
    markets_col_coin: 'Đồng',
    markets_col_price: 'Giá',
    markets_col_1h: '1g',
    markets_col_24h: '24g',
    markets_col_7d: '7n',
    markets_col_volume: 'Khối lượng 24g',
    markets_col_marketcap: 'Vốn hóa',
    markets_col_chart: '7 ngày qua',
    markets_buy: 'Mua',
    markets_showing: 'Hiển thị',
    markets_to: 'đến',
    markets_of: 'trong',
    markets_results: 'kết quả',
    markets_rows: 'Hàng',
    top_movers_title: 'Biến động mạnh',
    top_movers_live: 'Trực tiếp',
    crypto_label: 'CRYPTO',
    // Dashboard
    dash_win_rate: 'Tỷ lệ thắng',
    dash_balance: 'Số dư',
    dash_profit: 'Lãi/Lỗ ròng',
    dash_open: 'Đang mở',
    dash_open_trades: 'Giao dịch đang mở',
    dash_close_all: 'Đóng tất cả',
    dash_open_count: 'đang mở',
    dash_no_open_trades: 'Không có giao dịch đang mở',
    dash_col_asset: 'Tài sản',
    dash_col_type: 'Loại',
    dash_col_amount: 'Số tiền',
    dash_col_entry: 'Vào lệnh',
    dash_col_duration: 'Thời hạn',
    dash_col_remaining: 'Còn lại',
    dash_trade_history: 'Lịch sử giao dịch',
    dash_trades_count: 'giao dịch',
    dash_col_dir: 'Hướng',
    dash_col_exit: 'Ra lệnh',
    dash_col_pl: 'L/L',
    dash_col_result: 'Kết quả',
    dash_col_date: 'Ngày',
    dash_no_history: 'Chưa có lịch sử giao dịch',
    dash_load_more: 'Tải thêm',
    dash_loading: 'Đang tải...',
    dash_dur: 'T.hạn',
    dash_amt: 'S.tiền',
    dash_copying_trades: 'Bạn đang sao chép giao dịch Tradiglo',
    dash_deposit: 'Nạp tiền',
    dash_sell: 'Bán',
    dash_buy: 'Mua',
    dash_select_asset_hint: 'Chọn tài sản để xem biểu đồ',
    dash_select_asset_btn: 'Chọn tài sản',
    dash_payout: 'payout',
    dash_nav_trade: 'Giao dịch',
    dash_nav_history: 'Lịch sử',
    dash_nav_copytrade: 'Sao chép',
    dash_nav_affiliate: 'Liên kết',
    dash_nav_account: 'Tài khoản',
    dash_account_title: 'Tài khoản',
    dash_demo_balance: 'Số dư Demo',
    dash_real_balance: 'Số dư Thực',
    dash_close_all_confirm_title: 'Đóng tất cả giao dịch đang mở?',
    dash_close_all_confirm_desc: 'Tất cả giao dịch đang mở sẽ được đóng theo giá biểu đồ hiện tại.',
    dash_cancel: 'Hủy',
    dash_copy_following_badge: 'Đang theo dõi',
    dash_copy_win_rate: 'Tỷ lệ thắng',
    dash_copy_active_followers: 'Người theo dõi',
    dash_copy_min_balance: 'Số dư tối thiểu',
    dash_copy_history_title: 'Lịch sử sao chép',
    dash_copy_no_history: 'Chưa có lịch sử sao chép',
    dash_copy_guarantee: 'Bảo đảm 100% số dư khi thua',
    dash_copy_copying_msg: 'Bạn đang sao chép giao dịch Tradiglo',
    dash_copy_ready_msg: 'Sẵn sàng sao chép giao dịch Tradiglo?',
    dash_copy_stop_follow: 'Dừng theo dõi',
    dash_copy_follow_btn: 'Theo dõi Tradiglo',
    dash_copy_not_available: 'Sao chép giao dịch không khả dụng',
    dash_copy_invested: 'đã đầu tư',
    dash_copy_score_activity: 'Hoạt động',
    dash_copy_score_probability: 'Xác suất',
    dash_copy_score_reliability: 'Độ tin cậy',
    dash_copy_score_popularity: 'Độ phổ biến',
    dash_copy_score_experience: 'Kinh nghiệm',
    // Copy Trading Page
    ct_live_badge: 'Sao chép giao dịch trực tiếp — Được cung cấp bởi Tradiglo',
    ct_hero_title_1: 'Sao chép',
    ct_hero_title_2: 'Chiến lược của chúng tôi',
    ct_hero_subtitle: 'Theo dõi chiến lược giao dịch độc quyền của Tradiglo — tỷ lệ thắng được xác minh cao nhất trong ngành. Chỉ dành cho thành viên có số dư tối thiểu $',
    ct_checking_balance: 'Đang kiểm tra số dư...',
    ct_activate_btn: 'Kích hoạt sao chép giao dịch',
    ct_deposit_btn: 'Deposit to Unlock',
    ct_get_started_btn: 'เริ่มต้นฟรี',
    ct_sign_in_btn: 'เข้าสู่ระบบ',
    ct_balance_qualifies: 'ยอดเงินของคุณ $',
    ct_balance_qualifies_suffix: 'ผ่านเกณฑ์ ✓',
    ct_need_more: 'ต้องการอีก $',
    ct_need_more_suffix: 'เพื่อผ่านเกณฑ์ (ขั้นต่ำ $',
    ct_stat_win_rate: 'Tỷ lệ thắngรวม',
    ct_stat_win_rate_sub: '5年验证',
    ct_stat_total_trades: '总交易次数',
    ct_stat_total_trades_sub: '2020年1月 – 2025年3月',
    ct_stat_total_profit: '总利润',
    ct_stat_total_profit_sub: '累计',
    ct_stat_industry: '行业平均',
    ct_stat_industry_sub: '竞争对手',
    ct_guarantee_title: '90% 胜率保证',
    ct_guarantee_sub: '有5年经验证的业绩数据支持',
    ct_guarantee_desc: 'Tradiglo 保证所有跟单交易信号的最低 <strong>90% 胜率</strong> — 业内最高。',
    ct_compare_title: '我们的比较',
    ct_compare_sub: '与行业基准的胜率比较',
    ct_history_title: '5年交易历史',
    ct_history_sub: '经验证的月度业绩数据 — 2020年至2025年',
    ct_col_month: '月份',
    ct_col_trades: '交易次数',
    ct_col_wins: '获胜',
    ct_col_win_rate: '胜率',
    ct_col_profit: '利润',
    ct_how_title: '如何运作',
    ct_how_sub: '三个简单步骤开始复制我们的策略',
    ct_step1_title: '创建账户',
    ct_step1_desc: '免费注册并完成个人资料验证。',
    ct_step2_title: 'deposit $1,499+',
    ct_step2_desc: '向您的真实账户存入至少 $1,499 美元以解锁跟单交易。',
    ct_step3_title: '激活并赚取',
    ct_step3_desc: '启用跟单交易，我们的算法将自动为您镜像每笔交易。',
    ct_cta_title_1: '准备好复制',
    ct_cta_title_2: '最佳策略了吗？',
    ct_cta_sub: '加入已经从 Tradiglo',
    ct_cta_sub2: '胜率跟单交易系统中受益的数千名会员。',
    ct_cta_start_btn: '开始跟单交易',
    ct_cta_go_btn: '前往跟单交易仪表板',
    ct_cta_deposit_btn: '立即存款以解锁',
    // Screener Page
    sc_title: 'ตัวกรอง Kripto',
    sc_subtitle_prefix: 'กรอง',
    sc_subtitle_suffix: 'สกุลเงินดิจิทัลตามราคา ปริมาณ และการเคลื่อนไหว',
    sc_results: 'keputusan',
    sc_filters_btn: 'ตัวกรอง',
    sc_reset_btn: 'Set Semula',
    sc_refresh_btn: '↻ Muat Semula',
    sc_filter_movement: 'การเคลื่อนไหว',
    sc_filter_price: 'Julat Harga (USD)',
    sc_filter_volume: 'Volum 24j (dalam Juta)',
    sc_filter_change: 'Perubahan 24j (%)',
    sc_filter_marketcap: 'Min Permodalan Pasaran (dalam Juta)',
    sc_movement_all: 'semua',
    sc_movement_gainers: 'pemenang',
    sc_movement_losers: 'penakลุก',
    sc_error_suffix: '— API percuma CoinGecko mungkin mempunyai had kadar. Sila cuba lagi sebentar.',
    sc_col_name: 'ชื่อ',
    sc_col_price: 'ราคา',
    sc_col_24h: '24 ชม. %',
    sc_col_high: 'สูงสุด 24 ชม.',
    sc_col_low: 'ต่ำสุด 24 ชม.',
    sc_col_volume: 'ปริมาณ',
    sc_col_marketcap: 'มูลค่าตลาด',
    sc_no_match: 'ไม่พบสกุลเงินที่ตรงกับตัวกรอง ลองปรับเกณฑ์',
    // News Page
    news_title: 'Berita Kripto',
    news_subtitle: 'Berita mata wang kripto dan blockchain terkini, dikemas kini secara automatik',
    news_cat_all: 'Semua Kripto',
    news_cat_bitcoin: 'Bitcoin',
    news_cat_ethereum: 'Ethereum',
    news_cat_defi: 'DeFi',
    news_showing: 'Menunjukkan halaman',
    news_of: 'daripada',
    news_articles: 'artikel unik',
    news_read_more: 'Baca lagi',
    news_no_articles: 'ไม่พบบทความสำหรับหมวดหมู่นี้',
    news_prev: '‹ Sebelum',
    news_next: 'Seterusnya ›',
    // Affiliate/Referral Page
    aff_badge: 'โปรแกรม Afiliasi',
    aff_hero_title_1: 'Kiếm',
    aff_hero_title_2: 'cho mỗiคน bạn mời',
    aff_hero_subtitle: 'Mời bạn bè tham gia Tradiglo และ kiếm 25% tổng tiền nạp lần đầuของ họ — mãi mãi,ไม่มีวันหมดอายุ',
    aff_join_btn: 'Tham gia & Bắt đầu giới thiệu',
    aff_signin_btn: 'เข้าสู่ระบบแดชบอร์ด',
    aff_stat1_label: 'Hoa hồng trênเงิน nạpครั้งแรก',
    aff_stat2_label: 'Ngưỡng thanh toán tối thiểu',
    aff_stat3_label: 'Hiệu lực trọn đời',
    aff_how_title: 'วิธีการทำงาน',
    aff_how_sub: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มรับรายได้กับโปรแกรมพันธมิตร Tradiglo',
    aff_step1_title: 'สมัครและรับลิงก์ของคุณ',
    aff_step1_desc: 'สร้างบัญชี Tradiglo และรับลิงก์พันธมิตรเฉพาะจากแดชบอร์ด',
    aff_step2_title: 'เชิญเครือข่ายของคุณ',
    aff_step2_desc: 'แชร์ลิงก์พันธมิตรกับเพื่อน ครอบครัว หรือชุมชนผ่านช่องทางใดก็ได้',
    aff_step3_title: 'Họ tham gia & Nạpเงิน',
    aff_step3_desc: 'Khi ai đó đăng kýผ่านลิงก์ของคุณและnạpเงินครั้งแรก การแนะนำจะถูกบันทึก',
    aff_step4_title: 'รับรางวัล 25%',
    aff_step4_desc: 'คุณได้รับ 25% ของยอดnạpเงินครั้งแรกทั้งหมดของสมาชิกแต่ละคนที่คุณแนะนำ',
    aff_details_title: 'รายละเอียดโปรแกรม',
    aff_details_sub: 'ทุกสิ่งที่คุณต้องรู้เกี่ยวกับโปรแกรมพันธมิตร Tradiglo',
    aff_detail1_title: 'อัตราค่าคอมมิชชั่น',
    aff_detail1_value: '25% ของยอด depositครั้งแรก',
    aff_detail1_desc: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกทุกคนที่คุณแนะนำ',
    aff_detail2_title: 'ขั้นต่ำการถอน',
    aff_detail2_value: 'ขั้นต่ำ $500',
    aff_detail2_desc: 'รางวัลจะถูกปล่อยเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_detail3_title: 'ความถูกต้อง',
    aff_detail3_value: 'ตลอดชีพ — ไม่มีวันหมดอายุ',
    aff_detail3_desc: 'ความสัมพันธ์พันธมิตรของคุณถาวรตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_detail4_title: 'ขีดจำกัดพันธมิตร',
    aff_detail4_value: 'พันธมิตรไม่จำกัด',
    aff_detail4_desc: 'ไม่มีขีดจำกัดจำนวนคนที่คุณสามารถแนะนำได้ ยิ่งเชิญมาก ยิ่งได้มาก',
    aff_faq_title: 'คำถามที่พบบ่อย',
    aff_faq_sub: 'มีคำถาม? เรา有คำตอบ',
    aff_faq1_q: 'ฉันได้รับเท่าไหร่ต่อพันธมิตรหนึ่งคน?',
    aff_faq1_a: 'คุณได้รับ 25% ของยอด depositเงินครั้งแรกทั้งหมดของสมาชิกใหม่แต่ละคนที่คุณแนะนำสำเร็จ',
    aff_faq2_q: 'รางวัลจ่ายเมื่อไหร่?',
    aff_faq2_a: 'รางวัลจะจ่ายเมื่อรายได้พันธมิตรสะสมของคุณถึง $500',
    aff_faq3_q: 'โปรแกรมพันธมิตรใช้ได้นานแค่ไหน?',
    aff_faq3_a: 'โปรแกรมพันธมิตรของคุณใช้ได้ตราบเท่าที่ทั้งคุณและสมาชิกที่แนะนำยังเป็นสมาชิกที่ใช้งาน — ไม่มีวันหมดอายุ',
    aff_faq4_q: 'คุณสามารถแนะนำคนได้มากแค่ไหน?',
    aff_faq4_a: 'ไม่มีขีดจำกัด คุณสามารถแนะนำคนได้มากเท่าที่ต้องการและรับ 25% จากยอด depositครั้งแรกของแต่ละคน',
    aff_faq5_q: 'ฉันติดตามพันธมิตรของฉันได้อย่างไร?',
    aff_faq5_a: 'เมื่อคุณเป็นสมาชิก แดชบอร์ดของคุณจะแสดงลิงก์พันธมิตร จำนวนสมาชิกที่แนะนำ และยอดรางวัลสะสม',
    aff_faq6_q: 'สมาชิกที่ถูกแนะนำได้รับประโยชน์อะไรบ้าง?',
    aff_faq6_a: 'ใช่ — สมาชิกที่ถูกแนะนำอาจได้รับโบนัสต้อนรับเมื่อเข้าร่วม ตรวจสอบโปรเจ็กต์ล่าสุดในแดชบอร์ดของคุณ',
    aff_cta_title: 'พร้อมเริ่มรับรายได้แล้วหรือยัง?',
    aff_cta_sub: 'เข้าร่วม Tradiglo วันนี้ รับลิงก์พันธมิตรเฉพาะของคุณ และเริ่มรับ 25% จากสมาชิกทุกคนที่คุณนำเข้ามา — ตลอดชีพ',
    aff_cta_btn: 'สร้างบัญชีของคุณ',
    // Markets Page
    mkt_title: 'ตลาด',
    mkt_subtitle: 'ฝึกเทรดด้วยเงินเสมือน',
    mkt_demo_trades_left: 'การเทรดทดลอง',
    mkt_demo_trades_left_plural: 'การเทรดทดลอง',
    mkt_demo_trades_suffix: 'เหลือ',
    mkt_demo_balance: 'ยอดเงินบัญชีทดลอง',
    mkt_stat_total_trades: 'การเทรดทั้งหมด',
    mkt_stat_win_rate: 'อัตราชนะ',
    mkt_stat_total_profit: 'กำไรรวม',
    mkt_stat_open_positions: 'ตำแหน่งที่เปิดอยู่',
    mkt_trading_symbol: 'สัญลักษณ์การเทรด',
    mkt_live_market: 'ตลาดสด',
    mkt_potential_profit: 'potential profit',
    mkt_payout: 'ผลตอบแทน',
    mkt_open_positions_tab: 'ตำแหน่งที่เปิดอยู่',
    mkt_history_tab: 'ประวัติ',
    mkt_no_open_positions: 'ไม่มีตำแหน่งที่เปิดอยู่ วางการเทรดเพื่อเริ่มต้น',
    mkt_no_history: 'ยังไม่มีประวัติการเทรด',
    mkt_popular_assets: 'สินทรัพย์ยอดนิยม',
    mkt_popular_assets_sub: 'ติดตามและdagangkanหุ้น สกุลเงินดิจิทัล คู่ฟอเร็กซ์ และสินค้าโภคภัณฑ์ยอดนิยม',
    mkt_entry: 'ราคาเข้า:',
    mkt_invested: 'ลงทุน',
  },
};

interface LanguageContextType {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

export const useLanguage = () => useContext(LanguageContext);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>('en');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('tradiglo_lang') as LangCode;
      if (saved && translations[saved]) {
        setLangState(saved);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  const setLang = (code: LangCode) => {
    setLangState(code);
    try {
      localStorage.setItem('tradiglo_lang', code);
    } catch {
      // localStorage not available
    }
  };

  const t = (key: string): string => {
    return translations[lang]?.[key] ?? translations['en']?.[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
